import isPlainObject from 'lodash/isPlainObject';
import { nanoid } from 'nanoid';
import { equals, isEmpty, keys, mergeDeepLeft, pick, remove } from 'ramda';
import type { Location, NodeEntry, PathRef } from 'slate';
import { Editor, Element, Node, Path, Point, Range, Text, Transforms } from 'slate';
import type { DeepPartial, ValueOf } from 'ts-essentials';

import { linkColor } from '../../constants';
import type { CheckboxStateText } from '../../constants';
import type { UUID } from '../../types/type-helpers';
import { arrayEntries } from '../../util';
import { assertNeverSafely } from '../../util/ts-utils';
import {
  AlineTransforms,
  BreakVariant,
  EditingMode,
  getIsCancellableEdit,
  isBookmarkMarkerElement,
  isBreakElement,
  isCheckboxElement,
  isCheckboxOptionFieldElement,
  isDrawingElement,
  isEditWithSource,
  isInlineInstructionFieldElement,
  isInsertBookmarkEditEntry,
  isInsertBreakEditEntry,
  isInsertCheckboxEditEntry,
  isInsertDrawingEditEntry,
  isInsertEditEntry,
  isInsertInstructionFieldEditEntry,
  isInsertTabEditEntry,
  isInstructionFieldElement,
  isPageBreakElement,
  isParagraphElement,
  isParagraphGlyphEdit,
  isRemoveCheckboxEdit,
  isTabElement,
  isTableCellElement,
  isTableElement,
  isTableRowElement,
  isToggleCheckboxEdit,
  linkType,
  markParagraphAsSuggestedDelete,
  paragraphType,
  Queries,
  setGlyphSuggestedEdits,
  toParagraphNodeProps,
  transferGlyphPropsForParagraphMerge,
} from '../internal';
import type {
  AlineDescendant,
  AlineElement,
  AlineText,
  AlineTextFormats,
  BreakElement,
  CheckboxElement,
  DeleteEdit,
  DeleteEditEntry,
  DeleteParagraphEditEntry,
  DocEditEntry,
  DrawingElement,
  EditEntries,
  EditEntry,
  EditEntryGroup,
  FootersPartEditEntry,
  HeadersPartEditEntry,
  InsertBookmarkEditEntry,
  InsertBreakEditEntry,
  InsertCheckboxEditEntry,
  InsertDrawingEditEntry,
  InsertEdit,
  InsertEditEntry,
  InsertInstructionFieldEditEntry,
  InsertParagraphEditEntry,
  InsertTabEditEntry,
  InstructionFieldElement,
  LinkEditEntry,
  LinkElement,
  ParagraphElement,
  ParagraphFormatEditEntry,
  PartialParagraphProps,
  RemoveBreakEdit,
  RemoveBreakEditEntry,
  RemoveCheckboxEdit,
  RemoveCheckboxEditEntry,
  RemoveDrawingEdit,
  RemoveDrawingEditEntry,
  RemoveInstructionFieldEdit,
  RemoveInstructionFieldEditEntry,
  RemoveTabEdit,
  RemoveTabEditEntry,
  RemoveTableRowEdit,
  SuggestedCheckboxEdit,
  SuggestedDocEdit,
  SuggestedEdit,
  SuggestedEditValue,
  SuggestedFootersEdit,
  SuggestedHeadersEdit,
  SuggestedParagraphEdit,
  SuggestionSource,
  TabElement,
  TableElement,
  TableRowElement,
  TextFormatEdit,
  TextFormatEditEntry,
  ToggleCheckboxEdit,
  ToggleCheckboxEditEntry,
} from '../internal';

const TrackedChangesTransforms = {
  /**
   * Accepts all suggested edits outstanding in the document.
   */
  acceptAllSuggestedEdits: (
    editor: Editor,
    options: { authorId: UUID; editEntries: EditEntries; editingMode: EditingMode }
  ): SuggestionSource[] => {
    return AlineTransforms.withoutNormalizing<SuggestionSource[]>(editor, () => {
      const { authorId, editingMode } = options;

      const acceptedSources = AlineTransforms.acceptSuggestedEdits(editor, options);

      /**
       * This is tricky, but if some of the ranges we had in suggested edits get nulled out in the process of accepting all changes,
       * we may have nodes/ranges with un-accepted changes left over.  So, we re-calculate suggested edits after attempting to accept
       * all changes and then recursively call this function again with the new suggested edits, if any, so all suggested edits are
       * accepted.
       */
      const updatedEditEntries = Queries.sortedEditRanges(editor);
      if (!updatedEditEntries.length) return acceptedSources;

      const nextAcceptedSources = AlineTransforms.acceptAllSuggestedEdits(editor, {
        authorId,
        editEntries: updatedEditEntries,
        editingMode,
      });

      return [...acceptedSources, ...nextAcceptedSources];
    });
  },

  /**
   * Accepts all edits in a group atomically. Uses range refs to handle path
   * shifts as nodes are modified during iteration.
   */
  acceptEditGroup: (
    editor: Editor,
    options: { authorId: UUID; editGroup: EditEntryGroup; editingMode: EditingMode }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { authorId, editGroup, editingMode } = options;
      const editEntryRefs = editGroup.editEntries.map(editEntry => ({
        ...editEntry,
        range: Editor.rangeRef(editor, editEntry.range),
      }));

      for (const editEntryRef of editEntryRefs) {
        const range = editEntryRef.range.unref();
        if (!range) continue;

        AlineTransforms.acceptSuggestedEdit(editor, {
          authorId,
          editEntry: { ...editEntryRef, range },
          editingMode,
        });
      }
    });
  },

  /**
   * Accepts a suggested paragraph deletion (merge). For every paragraph in
   * the entry's range that carries a same-author `deleteParagraph` edit,
   * strips the edit and merges that paragraph with the one following it.
   */
  acceptSuggestedDeleteParagraph: (
    editor: Editor,
    options: { deleteParagraphEditEntry: DeleteParagraphEditEntry }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { deleteParagraphEditEntry } = options;
      const { edit, range } = deleteParagraphEditEntry;
      const paragraphPaths = removeMatchingGlyphSuggestedEdits(editor, range, {
        authorId: edit.authorId,
        type: 'deleteParagraph',
      });
      mergeEachWithNextParagraph(editor, paragraphPaths);
    });
  },

  /**
   * Accept a suggested deletion.
   */
  acceptSuggestedDeletion: (
    editor: Editor,
    options: {
      authorId: UUID;
      deleteEditEntry: DeleteEditEntry;
      editingMode: EditingMode;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { authorId, deleteEditEntry } = options;
      const { range } = deleteEditEntry;

      /**
       * Clear suggested edits from the nodes in the range first so, after the delete, we don't have a node left
       * with a suggested deletion.  Deleting the range usually leaves a single text node after the delete,
       * and the text node can have a suggested deletion if it's not cleared first.
       *
       * The `deleteParagraph` glyphs on pilcrows inside the range need no such
       * pass: the delete below merges those paragraphs, and the merge itself
       * moves the surviving pilcrow's glyphProps onto the survivor and drops
       * the merged-away pilcrows' marks (`transferGlyphPropsForParagraphMerge`).
       */
      Transforms.setNodes(
        editor,
        { suggestedEdits: [] },
        {
          at: range,
          hanging: true,
          match: node =>
            Text.isText(node) &&
            Array.isArray(node.suggestedEdits) &&
            !!node.suggestedEdits?.length,
          voids: true,
        }
      );

      AlineTransforms.deleteContent(editor, {
        at: range,
        authorId,
        editingMode: EditingMode.EDITING,
        hanging: true,
        voids: true,
      });
    });
  },

  /**
   * This is a generic way of accepting suggested elements on an element.
   *
   * It works whenever you want to accept suggested edits where the suggested
   * edit makes a change to one or more properties of an Element.
   *
   * It *CANNOT* be used for insertion or deletion.
   *
   * TODO: Switch more basic element property edits to this method.
   */
  acceptSuggestedDescendantEdit: <EL extends AlineDescendant>(
    editor: Editor,
    options: {
      edit: Exclude<NonNullable<EL['suggestedEdits']>[number], InsertEdit | DeleteEdit>;
      match: (node: Node) => node is EL;
      range: Range;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { edit, match, range } = options;
      const entries = Editor.nodes<EL>(editor, {
        at: range,
        match: node =>
          match(node) && 'suggestedEdits' in node && Array.isArray(node.suggestedEdits),
        mode: 'all',
        voids: true,
      });

      for (const [node, nodePath] of entries) {
        if (!node.suggestedEdits) continue;
        const nodeProps = Queries.descendantProps(node);
        const editIndex = node.suggestedEdits.findIndex(suggestedEdit =>
          equals(suggestedEdit, edit)
        );
        if (editIndex === -1) continue;

        const nextSuggestedEdits = remove(editIndex, 1, node.suggestedEdits);
        const nextNodePartialWithSuggestedEdits = {
          ...nodeProps,
          ...edit.value,
          suggestedEdits: nextSuggestedEdits,
        };

        Transforms.setNodes<AlineElement>(editor, nextNodePartialWithSuggestedEdits, {
          at: nodePath,
          voids: true,
        });
      }
    });
  },

  /**
   * Accepts a suggested document edit.
   */
  acceptSuggestedDocEdit: (
    editor: Editor,
    options: { docEditEntry: DocEditEntry; editingMode: EditingMode }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { docEditEntry } = options;
      const { edit } = docEditEntry;
      const [document, documentPath] = Queries.getDocument(editor);
      if (!document.suggestedEdits) return;
      const docEditIndex =
        document.suggestedEdits?.findIndex(suggestedEdit => equals(suggestedEdit, edit)) ?? -1;
      if (docEditIndex === -1) return;

      const existingEdit = document.suggestedEdits[docEditIndex];
      const docProps = pick(keys(existingEdit.value), document);
      const newProps = mergeDeepLeft(existingEdit.value, docProps);
      const newSuggestedEdits = remove(docEditIndex, 1, document.suggestedEdits);

      Transforms.setNodes(
        editor,
        { ...newProps, suggestedEdits: newSuggestedEdits },
        { at: documentPath }
      );
    });
  },

  /**
   * Accepts a single suggested edit.
   */
  acceptSuggestedEdit: (
    editor: Editor,
    options: { authorId: UUID; editEntry: EditEntry; editingMode: EditingMode }
  ) => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { authorId, editEntry, editingMode } = options;

      /**
       * Note: The only reason we pass a new object in to each transform below is to satisfy the TS compiler as to the types of the respective
       * edit entries.  For instance, for some reason the compiler narrows the type of editEntry.edit in the 'insertNode' case to be of type
       * InsertEdit, but doesn't narrow the edit entry to be of type InsertEditEntry.
       */
      switch (editEntry.edit.type) {
        case 'insertDrawing':
          AlineTransforms.acceptSuggestedInsertDrawing(editor, {
            editingMode,
            insertDrawingEntry: { edit: editEntry.edit, range: editEntry.range },
          });
          return;
        case 'removeDrawing':
          AlineTransforms.acceptSuggestedRemoveDrawing(editor, {
            editingMode,
            removeDrawingEntry: { edit: editEntry.edit, range: editEntry.range },
          });
          return;
        // This is deprecated. Intentionally returning null.
        case 'listFormat':
          return;
        case 'insertNode': {
          AlineTransforms.acceptSuggestedInsertion(editor, {
            editingMode,
            insertEditEntry: { edit: editEntry.edit, range: editEntry.range },
          });
          return;
        }
        case 'deleteNode': {
          AlineTransforms.acceptSuggestedDeletion(editor, {
            authorId,
            deleteEditEntry: { edit: editEntry.edit, range: editEntry.range },
            editingMode,
          });
          return;
        }
        case 'insertParagraph': {
          AlineTransforms.acceptSuggestedInsertParagraph(editor, {
            insertParagraphEditEntry: { edit: editEntry.edit, range: editEntry.range },
          });
          return;
        }
        case 'deleteParagraph': {
          AlineTransforms.acceptSuggestedDeleteParagraph(editor, {
            deleteParagraphEditEntry: { edit: editEntry.edit, range: editEntry.range },
          });
          return;
        }
        case 'insertBreak': {
          AlineTransforms.acceptSuggestedInsertBreak(editor, {
            editingMode,
            insertBreakEntry: { edit: editEntry.edit, range: editEntry.range },
          });
          return;
        }
        case 'removeBreak': {
          AlineTransforms.acceptSuggestedRemoveBreak(editor, {
            editingMode,
            removeBreakEntry: { edit: editEntry.edit, range: editEntry.range },
          });
          return;
        }
        case 'insertTab': {
          AlineTransforms.acceptSuggestedInsertTab(editor, {
            editingMode,
            insertTabEntry: { edit: editEntry.edit, range: editEntry.range },
          });
          return;
        }
        case 'insertBookmark': {
          AlineTransforms.acceptSuggestedInsertBookmark(editor, {
            insertBookmarkEntry: { edit: editEntry.edit, range: editEntry.range },
          });
          return;
        }
        case 'removeTab': {
          AlineTransforms.acceptSuggestedRemoveTab(editor, {
            editingMode,
            removeTabEntry: { edit: editEntry.edit, range: editEntry.range },
          });
          return;
        }
        case 'textFormat': {
          AlineTransforms.acceptSuggestedTextFormatEdit(editor, {
            editingMode,
            textFormatEditEntry: {
              edit: editEntry.edit,
              range: editEntry.range,
            },
          });
          return;
        }
        case 'insertLink':
        case 'removeLink':
        case 'linkEdit': {
          AlineTransforms.acceptSuggestedLinkEdit(editor, {
            editingMode,
            linkEditEntry: {
              edit: editEntry.edit,
              range: editEntry.range,
            },
          });
          return;
        }
        case 'paragraphFormat':
        case 'variant': {
          AlineTransforms.acceptSuggestedParagraphFormatEdit(editor, {
            editingMode,
            paragraphEditEntry: {
              edit: editEntry.edit,
              range: editEntry.range,
            },
          });
          return;
        }
        case 'styleSheet':
        case 'pageDimensions':
        case 'pageFormat': {
          AlineTransforms.acceptSuggestedDocEdit(editor, {
            docEditEntry: {
              edit: editEntry.edit,
              range: editEntry.range,
            },
            editingMode,
          });
          return;
        }
        case 'footerFormat': {
          AlineTransforms.acceptSuggestedFootersPartEdit(editor, {
            editingMode,
            footersPartEditEntry: {
              edit: editEntry.edit,
              range: editEntry.range,
            },
          });
          return;
        }
        case 'headerFormat': {
          AlineTransforms.acceptSuggestedHeadersPartEdit(editor, {
            editingMode,
            headersPartEditEntry: {
              edit: editEntry.edit,
              range: editEntry.range,
            },
          });
          return;
        }
        case 'tableFormat': {
          AlineTransforms.acceptSuggestedDescendantEdit(editor, {
            edit: editEntry.edit,
            match: isTableElement,
            range: editEntry.range,
          });
          return;
        }
        case 'tableCellFormat': {
          AlineTransforms.acceptSuggestedDescendantEdit(editor, {
            edit: editEntry.edit,
            match: isTableCellElement,
            range: editEntry.range,
          });
          return;
        }
        case 'insertInstructionField': {
          AlineTransforms.acceptSuggestedInsertInstructionField(editor, {
            editingMode,
            insertInstructionFieldEntry: {
              edit: editEntry.edit,
              range: editEntry.range,
            },
          });
          return;
        }
        case 'removeInstructionField': {
          AlineTransforms.acceptSuggestedRemoveInstructionField(editor, {
            editingMode,
            removeInstructionFieldEntry: {
              edit: editEntry.edit,
              range: editEntry.range,
            },
          });
          return;
        }
        case 'insertCheckbox': {
          AlineTransforms.acceptSuggestedInsertCheckbox(editor, {
            editingMode,
            insertCheckboxEntry: { edit: editEntry.edit, range: editEntry.range },
          });
          return;
        }
        case 'removeCheckbox': {
          AlineTransforms.acceptSuggestedRemoveCheckbox(editor, {
            editingMode,
            removeCheckboxEntry: { edit: editEntry.edit, range: editEntry.range },
          });
          return;
        }
        case 'toggleCheckbox': {
          AlineTransforms.acceptSuggestedToggleCheckbox(editor, {
            editingMode,
            toggleCheckboxEntry: { edit: editEntry.edit, range: editEntry.range },
          });
          return;
        }
        case 'insertTableCell': {
          AlineTransforms.acceptSuggestedInsertTableCell(editor, {
            editEntry,
          });
          return;
        }
        case 'removeTableCell': {
          AlineTransforms.acceptSuggestedRemoveTableCell(editor, {
            editEntry,
          });
          return;
        }
        case 'insertTableRow': {
          AlineTransforms.acceptSuggestedInsertTableRow(editor, {
            editEntry,
          });
          return;
        }
        case 'removeTableRow': {
          AlineTransforms.acceptSuggestedRemoveTableRow(editor, { editEntry });
          return;
        }
        default:
          assertNeverSafely(editEntry.edit);
      }
    });
  },

  /**
   * Accepts an array of suggested edits in a batch.
   */
  acceptSuggestedEdits: (
    editor: Editor,
    options: { authorId: UUID; editEntries: EditEntries; editingMode: EditingMode }
  ): SuggestionSource[] => {
    return AlineTransforms.withoutNormalizing<SuggestionSource[]>(editor, () => {
      const { authorId, editEntries, editingMode } = options;
      const acceptedSources: SuggestionSource[] = [];
      const editEntryRefs = editEntries.map(editEntry => ({
        ...editEntry,
        range: Editor.rangeRef(editor, editEntry.range),
      }));

      for (const editEntryRef of editEntryRefs) {
        const range = editEntryRef.range.unref();
        if (!range) continue;

        AlineTransforms.acceptSuggestedEdit(editor, {
          authorId,
          editEntry: { ...editEntryRef, range },
          editingMode,
        });
        if (isEditWithSource(editEntryRef.edit)) acceptedSources.push(editEntryRef.edit.source);
      }

      return acceptedSources;
    });
  },

  /**
   * Accepts a suggested footers part edit.
   */
  acceptSuggestedFootersPartEdit: (
    editor: Editor,
    options: {
      editingMode: EditingMode;
      footersPartEditEntry: FootersPartEditEntry;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { footersPartEditEntry } = options;
      const { edit } = footersPartEditEntry;
      const footersPartEntry = Queries.getFootersElement(editor);
      if (!footersPartEntry) return;

      const [footersPart, footersPartPath] = footersPartEntry;
      if (!footersPart.suggestedEdits) return;
      const editIndex = footersPart.suggestedEdits.findIndex(suggestedEdit =>
        equals(suggestedEdit, edit)
      );
      if (editIndex === -1) return;

      const existingEdit = footersPart.suggestedEdits[editIndex];
      const newSuggestedEdits = remove(editIndex, 1, footersPart.suggestedEdits);

      Transforms.setNodes(
        editor,
        { ...existingEdit.value, suggestedEdits: newSuggestedEdits },
        { at: footersPartPath }
      );
    });
  },

  /**
   * Accepts a suggested headers part edit.
   */
  acceptSuggestedHeadersPartEdit: (
    editor: Editor,
    options: {
      editingMode: EditingMode;
      headersPartEditEntry: HeadersPartEditEntry;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { headersPartEditEntry } = options;
      const { edit } = headersPartEditEntry;
      const headersPartEntry = Queries.getHeadersElement(editor);
      if (!headersPartEntry) return;

      const [headersPart, headersPartPath] = headersPartEntry;
      if (!headersPart.suggestedEdits) return;
      const editIndex = headersPart.suggestedEdits.findIndex(suggestedEdit =>
        equals(suggestedEdit, edit)
      );
      if (editIndex === -1) return;

      const existingEdit = headersPart.suggestedEdits[editIndex];
      const newSuggestedEdits = remove(editIndex, 1, headersPart.suggestedEdits);

      Transforms.setNodes(
        editor,
        { ...existingEdit.value, suggestedEdits: newSuggestedEdits },
        { at: headersPartPath }
      );
    });
  },

  /**
   * Accepts a suggested bookmark insertion by clearing the insert edit. The
   * bookmark stays in the document.
   */
  acceptSuggestedInsertBookmark: (
    editor: Editor,
    options: { insertBookmarkEntry: InsertBookmarkEditEntry }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { insertBookmarkEntry } = options;
      const { edit, range } = insertBookmarkEntry;
      const entries = [
        ...Editor.nodes(editor, {
          at: range,
          match: node =>
            isBookmarkMarkerElement(node) &&
            Array.isArray(node.suggestedEdits) &&
            !isEmpty(node.suggestedEdits),
          voids: true,
        }),
      ];

      const above = Editor.above(editor, {
        at: Range.start(range),
        match: isBookmarkMarkerElement,
        voids: true,
      });
      if (above && !entries.some(([, path]) => Path.equals(path, above[1]))) entries.push(above);

      for (const [node, path] of entries) {
        if (!isBookmarkMarkerElement(node) || !node.suggestedEdits) continue;
        const editIndex = node.suggestedEdits.findIndex(suggestedEdit =>
          equals(suggestedEdit, edit)
        );
        if (editIndex === -1) continue;

        Transforms.setNodes(
          editor,
          { suggestedEdits: remove(editIndex, 1, node.suggestedEdits) },
          { at: path, voids: true }
        );
      }
    });
  },

  /**
   * Accepts a suggested break (e.g., page break).
   */
  acceptSuggestedInsertBreak: (
    editor: Editor,
    options: { editingMode: EditingMode; insertBreakEntry: InsertBreakEditEntry }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { insertBreakEntry } = options;
      const { edit, range } = insertBreakEntry;
      const breakEntries = [
        ...Editor.nodes<BreakElement>(editor, {
          at: range,
          match: node =>
            isBreakElement(node) &&
            Array.isArray(node.suggestedEdits) &&
            !isEmpty(node.suggestedEdits),
        }),
      ];

      for (const [node, path] of breakEntries) {
        if (!node.suggestedEdits) continue;
        const breakEditIndex = node.suggestedEdits.findIndex(suggestedEdit =>
          equals(suggestedEdit, edit)
        );
        if (breakEditIndex === -1) continue;

        const newSuggestedEdits = remove(breakEditIndex, 1, node.suggestedEdits);

        Transforms.setNodes(
          editor,
          { suggestedEdits: newSuggestedEdits },
          { at: path, voids: true }
        );
      }
    });
  },

  acceptSuggestedInsertCheckbox: (
    editor: Editor,
    options: { editingMode: EditingMode; insertCheckboxEntry: InsertCheckboxEditEntry }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { insertCheckboxEntry } = options;
      const { edit, range } = insertCheckboxEntry;
      const checkboxEntries = [
        ...Editor.nodes<CheckboxElement>(editor, {
          at: range,
          match: node =>
            isCheckboxElement(node) &&
            Array.isArray(node.suggestedEdits) &&
            !isEmpty(node.suggestedEdits),
        }),
      ];

      for (const [node, path] of checkboxEntries) {
        const checkboxEditIndex =
          node.suggestedEdits?.findIndex(suggestedEdit => equals(suggestedEdit, edit)) ?? -1;
        if (checkboxEditIndex === -1) continue;

        const newSuggestedEdits = remove(checkboxEditIndex, 1, node.suggestedEdits);

        Transforms.setNodes(
          editor,
          { suggestedEdits: newSuggestedEdits },
          { at: path, voids: true }
        );
      }
    });
  },

  /**
   * Accepts a suggested image insertion.
   */
  acceptSuggestedInsertDrawing: (
    editor: Editor,
    options: { editingMode: EditingMode; insertDrawingEntry: InsertDrawingEditEntry }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { insertDrawingEntry } = options;
      const { edit, range } = insertDrawingEntry;
      const drawingEntries = [
        ...Editor.nodes<DrawingElement>(editor, {
          at: range,
          match: node =>
            isDrawingElement(node) &&
            Array.isArray(node.suggestedEdits) &&
            !isEmpty(node.suggestedEdits),
        }),
      ];

      for (const [node, path] of drawingEntries) {
        if (!node.suggestedEdits) continue;
        const drawingEditIndex = node.suggestedEdits.findIndex(suggestedEdit =>
          equals(suggestedEdit, edit)
        );
        if (drawingEditIndex === -1) continue;

        const newSuggestedEdits = remove(drawingEditIndex, 1, node.suggestedEdits);

        Transforms.setNodes(
          editor,
          { suggestedEdits: newSuggestedEdits },
          { at: path, voids: true }
        );
      }
    });
  },

  /**
   * Accepts a suggested instruction field insertion.
   */
  acceptSuggestedInsertInstructionField: (
    editor: Editor,
    options: {
      editingMode: EditingMode;
      insertInstructionFieldEntry: InsertInstructionFieldEditEntry;
    }
  ) => {
    const { insertInstructionFieldEntry } = options;
    const { edit, range } = insertInstructionFieldEntry;

    const instructionFieldEntries = [
      ...Editor.nodes<InstructionFieldElement>(editor, {
        at: range,
        match: node =>
          isInstructionFieldElement(node) &&
          node.instruction === edit.instruction &&
          Array.isArray(node.suggestedEdits) &&
          !isEmpty(node.suggestedEdits),
      }),
    ];

    for (const [node, path] of instructionFieldEntries) {
      if (!node.suggestedEdits) continue;
      const instructionFieldEditIndex = node.suggestedEdits.findIndex(suggestedEdit =>
        equals(suggestedEdit, edit)
      );
      if (instructionFieldEditIndex === -1) continue;

      const newSuggestedEdits = remove(instructionFieldEditIndex, 1, node.suggestedEdits);

      Transforms.setNodes(editor, { suggestedEdits: newSuggestedEdits }, { at: path, voids: true });
    }
  },

  /**
   * Accepts a suggested paragraph insertion (split). Strips the
   * `insertParagraph` edit from every paragraph in the entry's range carrying
   * a same-author edit. Paragraphs stay split.
   */
  acceptSuggestedInsertParagraph: (
    editor: Editor,
    options: { insertParagraphEditEntry: InsertParagraphEditEntry }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { insertParagraphEditEntry } = options;
      const { edit, range } = insertParagraphEditEntry;
      removeMatchingGlyphSuggestedEdits(editor, range, {
        authorId: edit.authorId,
        type: 'insertParagraph',
      });
    });
  },

  /**
   * Accepts a suggested tab
   */
  acceptSuggestedInsertTab: (
    editor: Editor,
    options: { editingMode: EditingMode; insertTabEntry: InsertTabEditEntry }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { insertTabEntry } = options;
      const { edit, range } = insertTabEntry;
      const entries = [
        ...Editor.nodes<TabElement>(editor, {
          at: range,
          match: node =>
            isTabElement(node) &&
            Array.isArray(node.suggestedEdits) &&
            !isEmpty(node.suggestedEdits),
          voids: true,
        }),
      ];

      for (const [node, path] of entries) {
        if (!node.suggestedEdits) continue;
        const editIndex = node.suggestedEdits.findIndex(suggestedEdit =>
          equals(suggestedEdit, edit)
        );
        if (editIndex === -1) continue;

        const newSuggestedEdits = remove(editIndex, 1, node.suggestedEdits);

        Transforms.setNodes(
          editor,
          { suggestedEdits: newSuggestedEdits },
          { at: path, voids: true }
        );
      }
    });
  },

  /**
   * Accepts a suggested insertTableCell edit by removing the edit marker from
   * the cell's suggestedEdits. The cell itself stays in the document.
   */
  acceptSuggestedInsertTableCell: (editor: Editor, options: { editEntry: EditEntry }): void => {
    removeEditFromTableCells(editor, options.editEntry);
  },

  /**
   * Accepts a suggested table row insertion.
   * Removes the insertTableRow suggested edits from all rows in the edit range.
   */
  acceptSuggestedInsertTableRow: (editor: Editor, options: { editEntry: EditEntry }): void => {
    removeEditsFromTableRows(editor, options.editEntry);
  },

  /**
   * Accepts a suggested insertion.
   */
  acceptSuggestedInsertion: (
    editor: Editor,
    options: { editingMode: EditingMode; insertEditEntry: InsertEditEntry }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { insertEditEntry } = options;
      const { edit, range } = insertEditEntry;
      const insertedEntries = Editor.nodes<AlineDescendant>(editor, {
        at: range,
        match: node =>
          (!Editor.isEditor(node) &&
            Array.isArray(node.suggestedEdits) &&
            node.suggestedEdits?.some(suggestedEdit => equals(suggestedEdit, edit))) ??
          false,
        voids: true,
      });

      for (const [node, path] of insertedEntries) {
        if (!node.suggestedEdits) continue;
        const insertEditIndex = node.suggestedEdits.findIndex(suggestedEdit =>
          equals(suggestedEdit, edit)
        );
        if (insertEditIndex === -1) continue;

        const newSuggestedEdits = remove(insertEditIndex, 1, node.suggestedEdits);

        Transforms.setNodes(
          editor,
          { suggestedEdits: newSuggestedEdits },
          { at: path, voids: true }
        );
      }
    });
  },

  /**
   * Accepts a suggested link edit.
   */
  acceptSuggestedLinkEdit: (
    editor: Editor,
    options: { editingMode: EditingMode; linkEditEntry: LinkEditEntry }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { editingMode, linkEditEntry } = options;
      const { edit, range } = linkEditEntry;
      const linkEntries = [
        ...Editor.nodes<LinkElement>(editor, {
          at: range,
          match: node =>
            Element.isElement(node) &&
            node.type === linkType &&
            Array.isArray(node.suggestedEdits) &&
            !isEmpty(node.suggestedEdits),
          voids: true,
        }),
      ];

      switch (edit.type) {
        case 'insertLink': {
          /**
           * If we're accepting a link insertion, the link is already in place.  All we have to do is remove the suggested insertion
           * and leave the link.
           */
          for (const [link, linkPath] of linkEntries) {
            if (!link.suggestedEdits) continue;
            const editIndex = link.suggestedEdits.findIndex(suggestedEdit =>
              equals(suggestedEdit, edit)
            );
            if (editIndex === -1) continue;

            const newSuggestedEdits = remove(editIndex, 1, link.suggestedEdits);

            Transforms.setNodes(
              editor,
              { suggestedEdits: newSuggestedEdits },
              { at: linkPath, voids: true }
            );
          }

          break;
        }
        case 'removeLink': {
          const pathRefs = linkEntries.map(([, linkPath]) => Editor.pathRef(editor, linkPath));

          /**
           * If we're accepting a link removal, we need to delete the link since it still exists.
           */
          for (const pathRef of pathRefs) {
            const linkPath = pathRef.unref();
            if (!linkPath) continue;

            // No need to do anything with suggested edits since the link node will be unwrapped/removed inside remove link.
            AlineTransforms.unwrapLink(editor, { editingMode, linkPath });
          }

          break;
        }
        case 'linkEdit': {
          /**
           * If we're accepting a link edit, we modify the suggested edits and merge the suggestion into
           * the link node.
           */
          for (const [link, linkPath] of linkEntries) {
            if (!link.suggestedEdits) continue;
            const editIndex = link.suggestedEdits.findIndex(suggestedEdit =>
              equals(suggestedEdit, edit)
            );
            if (editIndex === -1) continue;

            const newSuggestedEdits = remove(editIndex, 1, link.suggestedEdits);

            Transforms.setNodes(
              editor,
              { ...edit.value, suggestedEdits: newSuggestedEdits },
              { at: linkPath, voids: true }
            );
          }

          break;
        }
      }
    });
  },

  /**
   * Accepts a suggested paragraph edit.
   */
  acceptSuggestedParagraphFormatEdit: (
    editor: Editor,
    options: {
      editingMode: EditingMode;
      paragraphEditEntry: ParagraphFormatEditEntry;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { paragraphEditEntry } = options;
      const { edit, range } = paragraphEditEntry;
      const paragraphEntries = Editor.nodes<ParagraphElement>(editor, {
        at: range,
        match: node =>
          Element.isElement(node) &&
          node.type === paragraphType &&
          Array.isArray(node.suggestedEdits) &&
          !isEmpty(node.suggestedEdits),
        voids: true,
      });
      const pathRefs = paragraphEntries.map(([, path]) => Editor.pathRef(editor, path)).toArray();

      for (const pathRef of pathRefs) {
        const paragraphPath = pathRef.unref();
        if (!paragraphPath) continue;

        const paragraph = Node.get(editor, paragraphPath);
        if (!isParagraphElement(paragraph) || !paragraph.suggestedEdits) continue;

        const editIndex = paragraph.suggestedEdits.findIndex(suggestedEdit =>
          equals(suggestedEdit, edit)
        );
        if (editIndex === -1) continue;

        const existingEdit = paragraph.suggestedEdits[editIndex];
        const value: PartialParagraphProps = { ...existingEdit.value };
        const newSuggestedEdits = remove(editIndex, 1, paragraph.suggestedEdits);

        Transforms.setNodes(
          editor,
          { ...toParagraphNodeProps(value), suggestedEdits: newSuggestedEdits },
          { at: paragraphPath, voids: true }
        );
      }
    });
  },

  /**
   * Accepts a suggested remove break (e.g., page break).
   */
  acceptSuggestedRemoveBreak: (
    editor: Editor,
    options: { editingMode: EditingMode; removeBreakEntry: RemoveBreakEditEntry }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { removeBreakEntry } = options;
      const { range } = removeBreakEntry;

      Transforms.removeNodes(editor, {
        at: range,
        hanging: true,
        match: isBreakElement,
        voids: true,
      });
    });
  },

  acceptSuggestedRemoveCheckbox: (
    editor: Editor,
    options: { editingMode: EditingMode; removeCheckboxEntry: RemoveCheckboxEditEntry }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { removeCheckboxEntry } = options;
      const { range } = removeCheckboxEntry;

      const checkboxEntries = [
        ...Editor.nodes<CheckboxElement>(editor, {
          at: range,
          match: isCheckboxElement,
          voids: true,
        }),
      ];

      for (const [, checkboxPath] of checkboxEntries) {
        const optionEntry = Editor.above(editor, {
          at: checkboxPath,
          match: isCheckboxOptionFieldElement,
          voids: true,
        });

        Transforms.removeNodes(editor, {
          at: optionEntry ? optionEntry[1] : checkboxPath,
          voids: true,
        });
      }
    });
  },

  /**
   * Accepts a suggested remove drawing.
   */
  acceptSuggestedRemoveDrawing: (
    editor: Editor,
    options: { editingMode: EditingMode; removeDrawingEntry: RemoveDrawingEditEntry }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { removeDrawingEntry } = options;
      const { range } = removeDrawingEntry;

      Transforms.removeNodes(editor, {
        at: range,
        hanging: true,
        match: isDrawingElement,
        voids: true,
      });
    });
  },

  /**
   * Accepts a suggested remove instruction field.
   */
  acceptSuggestedRemoveInstructionField: (
    editor: Editor,
    options: {
      editingMode: EditingMode;
      removeInstructionFieldEntry: RemoveInstructionFieldEditEntry;
    }
  ): void => {
    Editor.withoutNormalizing(editor, () => {
      const { removeInstructionFieldEntry } = options;
      const { range } = removeInstructionFieldEntry;

      Transforms.removeNodes(editor, {
        at: range,
        hanging: true,
        match: isInstructionFieldElement,
        voids: true,
      });
    });
  },

  /**
   * Accepts a suggested remove tab.
   */
  acceptSuggestedRemoveTab: (
    editor: Editor,
    options: { editingMode: EditingMode; removeTabEntry: RemoveTabEditEntry }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { removeTabEntry } = options;
      const { range } = removeTabEntry;

      Transforms.removeNodes(editor, {
        at: range,
        hanging: true,
        match: isTabElement,
        voids: true,
      });
    });
  },

  /**
   * Accepts a suggested removeTableCell edit by removing the cell from the
   * document (the removal is confirmed).
   */
  acceptSuggestedRemoveTableCell: (editor: Editor, options: { editEntry: EditEntry }): void => {
    removeTableCellsInRange(editor, options.editEntry);
  },

  /**
   * Accepts a suggested table row removal
   */
  acceptSuggestedRemoveTableRow: (editor: Editor, options: { editEntry: EditEntry }): void => {
    removeTableRowsInRange(editor, options.editEntry);
  },

  /**
   * Accept an outstanding text format edit.
   */
  acceptSuggestedTextFormatEdit: (
    editor: Editor,
    options: { editingMode: EditingMode; textFormatEditEntry: TextFormatEditEntry }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { textFormatEditEntry } = options;
      const { edit, range } = textFormatEditEntry;
      const textEntries = [
        ...Editor.nodes<AlineText>(editor, {
          at: range,
          match: node =>
            Text.isText(node) &&
            Array.isArray(node.suggestedEdits) &&
            !isEmpty(node.suggestedEdits),
          voids: true,
        }),
      ];

      for (const [textNode, textPath] of textEntries) {
        if (!textNode.suggestedEdits) continue;
        const editIndex = textNode.suggestedEdits.findIndex(textEdit => equals(textEdit, edit));
        if (editIndex === -1) continue;

        const newSuggestedEdits = remove(editIndex, 1, textNode.suggestedEdits);

        Transforms.setNodes(
          editor,
          { ...edit.value, suggestedEdits: newSuggestedEdits },
          { at: textPath, voids: true }
        );
      }
    });
  },

  acceptSuggestedToggleCheckbox: (
    editor: Editor,
    options: { editingMode: EditingMode; toggleCheckboxEntry: ToggleCheckboxEditEntry }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { edit, range } = options.toggleCheckboxEntry;
      forMatchingCheckboxEdit(editor, { edit, range }, (path, editIndex, checkbox) => {
        AlineTransforms.insertText(editor, {
          at: Editor.range(editor, path.concat(0)),
          authorId: edit.authorId,
          editingMode: EditingMode.EDITING,
          select: false,
          textToInsert: edit.value.to,
          voids: true,
        });
        removeCheckboxEditAt(editor, path, editIndex, checkbox);
      });
    });
  },

  /**
   * Rejects all suggested edits outstanding in the document.
   */
  rejectAllSuggestedEdits: (
    editor: Editor,
    options: { authorId: UUID; editEntries: EditEntries; editingMode: EditingMode }
  ): SuggestionSource[] => {
    return AlineTransforms.withoutNormalizing<SuggestionSource[]>(editor, () => {
      const { authorId, editingMode } = options;

      const rejectedSources = AlineTransforms.rejectSuggestedEdits(editor, options);

      /**
       * See note in AlineTransforms.acceptAllSuggestedEdits for why we get updated suggested edits.
       */
      const updatedEditEntries = Queries.sortedEditRanges(editor);
      if (!updatedEditEntries.length) return rejectedSources;

      const nextRejectedSources = AlineTransforms.rejectAllSuggestedEdits(editor, {
        authorId,
        editEntries: updatedEditEntries,
        editingMode,
      });

      return [...rejectedSources, ...nextRejectedSources];
    });
  },

  /**
   * Rejects all edits in a group atomically. Uses range refs to handle path
   * shifts as nodes are modified during iteration.
   */
  rejectEditGroup: (
    editor: Editor,
    options: { authorId: UUID; editGroup: EditEntryGroup; editingMode: EditingMode }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { authorId, editGroup, editingMode } = options;
      const orderedEntries = orderEntriesForRejection(editGroup.editEntries);
      const editEntryRefs = orderedEntries.map(editEntry => ({
        ...editEntry,
        range: Editor.rangeRef(editor, editEntry.range),
      }));

      for (const editEntryRef of editEntryRefs) {
        const range = editEntryRef.range.unref();
        if (!range) continue;

        AlineTransforms.rejectSuggestedEdit(editor, {
          authorId,
          editEntry: { ...editEntryRef, range },
          editingMode,
        });
      }
    });
  },

  /**
   * Rejects a suggested paragraph deletion (merge). Strips every same-author
   * `deleteParagraph` edit in the entry's range; the paragraphs stay
   * separate.
   */
  rejectSuggestedDeleteParagraph: (
    editor: Editor,
    options: { deleteParagraphEditEntry: DeleteParagraphEditEntry }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { deleteParagraphEditEntry } = options;
      const { edit, range } = deleteParagraphEditEntry;
      removeMatchingGlyphSuggestedEdits(editor, range, {
        authorId: edit.authorId,
        type: 'deleteParagraph',
      });
    });
  },

  /**
   * Rejects a suggested deletion.
   */
  rejectSuggestedDeletion: (
    editor: Editor,
    options: { deleteEditEntry: DeleteEditEntry; editingMode: EditingMode }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { deleteEditEntry } = options;
      const { edit, range } = deleteEditEntry;
      const nodeEntries = Editor.nodes<AlineDescendant>(editor, {
        at: range,
        match: node =>
          (!Editor.isEditor(node) &&
            Array.isArray(node.suggestedEdits) &&
            node.suggestedEdits?.some(suggestedEdit => equals(suggestedEdit, edit))) ??
          false,
        voids: true,
      });

      for (const [node, path] of nodeEntries) {
        if (!node.suggestedEdits) continue;
        const editIndex = node.suggestedEdits.findIndex(suggestedEdit =>
          equals(suggestedEdit, edit)
        );
        if (editIndex === -1) continue;

        const newSuggestedEdits = remove(editIndex, 1, node.suggestedEdits);

        Transforms.setNodes(
          editor,
          { suggestedEdits: newSuggestedEdits },
          { at: path, voids: true }
        );
      }
    });
  },

  /**
   * Rejects a suggested edit on an AlineDescendant.
   */
  rejectSuggestedDescendantEdit: <EL extends AlineDescendant>(
    editor: Editor,
    options: {
      edit: Exclude<NonNullable<EL['suggestedEdits']>[number], InsertEdit | DeleteEdit>;
      match: (node: Node) => node is EL;
      range: Range;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { edit, match, range } = options;
      const entries = Editor.nodes<EL>(editor, {
        at: range,
        match: node =>
          match(node) && 'suggestedEdits' in node && Array.isArray(node.suggestedEdits),
        mode: 'all',
        voids: true,
      });

      for (const [node, nodePath] of entries) {
        if (!node.suggestedEdits) continue;
        const editIndex = node.suggestedEdits.findIndex(suggestedEdit =>
          equals(suggestedEdit, edit)
        );
        if (editIndex === -1) continue;

        const nextSuggestedEdits: EL['suggestedEdits'] = remove(editIndex, 1, node.suggestedEdits);

        Transforms.setNodes<AlineElement>(
          editor,
          { suggestedEdits: nextSuggestedEdits },
          { at: nodePath, voids: true }
        );
      }
    });
  },

  /**
   * Rejects a suggested document edit.
   */
  rejectSuggestedDocEdit: (
    editor: Editor,
    options: { docEditEntry: DocEditEntry; editingMode: EditingMode }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { docEditEntry, editingMode } = options;
      const { edit } = docEditEntry;
      const [document, documentPath] = Queries.getDocument(editor);
      if (!document.suggestedEdits) return;
      const editIndex = document.suggestedEdits.findIndex(suggestedEdit =>
        equals(suggestedEdit, edit)
      );
      if (editIndex === -1) return;

      const newSuggestedEdits = remove(editIndex, 1, document.suggestedEdits);

      Transforms.setNodes(
        editor,
        { suggestedEdits: newSuggestedEdits },
        { at: documentPath, voids: true }
      );

      /**
       * If a style sheet edit is being rejected, we need to make sure to update all text in the document back to the
       * way the original stylesheet was before the edit.  IMPORTANT, do this LAST so that the document style sheet is up
       * to date - i.e., the suggested style sheet edit is already removed from the document node via the transforms above.
       */
      if (edit.type === 'styleSheet') {
        const styleVariantKeys = keys(edit.value.styleSheet);
        const paragraphEntries = Editor.nodes<ParagraphElement>(editor, {
          at: [],
          match: node => {
            if (!isParagraphElement(node)) return false;

            const activeStyleVariant = Queries.renderedParagraphStyleVariant(node);

            return styleVariantKeys.includes(activeStyleVariant);
          },
          voids: true,
        });

        for (const [paragraph, paragraphPath] of paragraphEntries) {
          /**
           * Simply applying the styles from the style variant already carried on the paragraph should make all text in the paragraph have the correct
           * styles after rejecting the style sheet edit.
           */
          const styleVariant = Queries.renderedParagraphStyleVariant(paragraph);

          AlineTransforms.applyStyleVariantToText(editor, {
            editingMode,
            isUpdateToMatch: true,
            paragraphEntry: [paragraph, paragraphPath],
            styleVariant,
          });
        }
      }
    });
  },

  /**
   * Rejects a single suggested edit.
   */
  rejectSuggestedEdit: (
    editor: Editor,
    options: { authorId: UUID; editEntry: EditEntry; editingMode: EditingMode }
  ) => {
    return AlineTransforms.withoutNormalizing(editor, () => {
      const { authorId, editEntry, editingMode } = options;

      // See comment above in AlineTransforms.acceptSuggestedEdit for why we provide a new edit entry object for each transform.
      switch (editEntry.edit.type) {
        case 'insertDrawing':
          AlineTransforms.rejectSuggestedInsertDrawing(editor, {
            editingMode,
            insertDrawingEntry: { edit: editEntry.edit, range: editEntry.range },
          });
          return;
        case 'removeDrawing':
          AlineTransforms.rejectSuggestedRemoveDrawing(editor, {
            editingMode,
            removeDrawingEntry: { edit: editEntry.edit, range: editEntry.range },
          });
          return;
        case 'insertNode': {
          AlineTransforms.rejectSuggestedInsertion(editor, {
            authorId,
            editingMode,
            insertEditEntry: { edit: editEntry.edit, range: editEntry.range },
          });
          return;
        }
        case 'deleteNode': {
          AlineTransforms.rejectSuggestedDeletion(editor, {
            deleteEditEntry: { edit: editEntry.edit, range: editEntry.range },
            editingMode,
          });
          return;
        }
        case 'insertParagraph': {
          AlineTransforms.rejectSuggestedInsertParagraph(editor, {
            insertParagraphEditEntry: { edit: editEntry.edit, range: editEntry.range },
          });
          return;
        }
        case 'deleteParagraph': {
          AlineTransforms.rejectSuggestedDeleteParagraph(editor, {
            deleteParagraphEditEntry: { edit: editEntry.edit, range: editEntry.range },
          });
          return;
        }
        case 'insertBreak': {
          AlineTransforms.rejectSuggestedInsertBreak(editor, {
            editingMode,
            insertBreakEntry: { edit: editEntry.edit, range: editEntry.range },
          });
          return;
        }
        case 'removeBreak': {
          AlineTransforms.rejectSuggestedRemoveBreak(editor, {
            editingMode,
            removeBreakEntry: { edit: editEntry.edit, range: editEntry.range },
          });
          return;
        }
        case 'insertTab': {
          AlineTransforms.rejectSuggestedInsertTab(editor, {
            editingMode,
            insertTabEntry: { edit: editEntry.edit, range: editEntry.range },
          });
          return;
        }
        case 'insertBookmark': {
          AlineTransforms.rejectSuggestedInsertBookmark(editor, {
            insertBookmarkEntry: { edit: editEntry.edit, range: editEntry.range },
          });
          return;
        }
        case 'removeTab': {
          AlineTransforms.rejectSuggestedRemoveTab(editor, {
            editingMode,
            removeTabEntry: { edit: editEntry.edit, range: editEntry.range },
          });
          return;
        }
        case 'textFormat': {
          AlineTransforms.rejectSuggestedTextFormatEdit(editor, {
            editingMode,
            textFormatEditEntry: {
              edit: editEntry.edit,
              range: editEntry.range,
            },
          });
          return;
        }
        case 'insertLink':
        case 'removeLink':
        case 'linkEdit': {
          AlineTransforms.rejectSuggestedLinkEdit(editor, {
            editingMode,
            linkEditEntry: {
              edit: editEntry.edit,
              range: editEntry.range,
            },
          });
          return;
        }
        // Deprecated.  Intentionally returning null to be consistent with return type.
        case 'listFormat':
          return null;
        case 'paragraphFormat':
        case 'variant': {
          AlineTransforms.rejectSuggestedParagraphFormatEdit(editor, {
            editingMode,
            paragraphEditEntry: {
              edit: editEntry.edit,
              range: editEntry.range,
            },
          });
          return;
        }
        case 'pageDimensions':
        case 'pageFormat':
        case 'styleSheet': {
          AlineTransforms.rejectSuggestedDocEdit(editor, {
            docEditEntry: {
              edit: editEntry.edit,
              range: editEntry.range,
            },
            editingMode,
          });
          return;
        }
        case 'footerFormat': {
          AlineTransforms.rejectSuggestedFootersPartEdit(editor, {
            editingMode,
            footersPartEditEntry: {
              edit: editEntry.edit,
              range: editEntry.range,
            },
          });
          return;
        }
        case 'headerFormat': {
          AlineTransforms.rejectSuggestedHeadersPartEdit(editor, {
            editingMode,
            headersPartEditEntry: {
              edit: editEntry.edit,
              range: editEntry.range,
            },
          });
          return;
        }
        case 'tableFormat': {
          AlineTransforms.rejectSuggestedDescendantEdit(editor, {
            edit: editEntry.edit,
            match: isTableElement,
            range: editEntry.range,
          });
          return;
        }
        case 'tableCellFormat': {
          AlineTransforms.rejectSuggestedDescendantEdit(editor, {
            edit: editEntry.edit,
            match: isTableCellElement,
            range: editEntry.range,
          });
          return;
        }
        case 'insertInstructionField': {
          AlineTransforms.rejectSuggestedInsertInstructionField(editor, {
            editingMode,
            insertInstructionFieldEntry: { edit: editEntry.edit, range: editEntry.range },
          });
          return;
        }
        case 'removeInstructionField': {
          AlineTransforms.rejectSuggestedRemoveInstructionField(editor, {
            editingMode,
            removeInstructionFieldEntry: { edit: editEntry.edit, range: editEntry.range },
          });
          return;
        }
        case 'insertCheckbox': {
          AlineTransforms.rejectSuggestedInsertCheckbox(editor, {
            editingMode,
            insertCheckboxEntry: { edit: editEntry.edit, range: editEntry.range },
          });
          return;
        }
        case 'removeCheckbox':
        case 'toggleCheckbox': {
          rejectMatchingCheckboxEdit(editor, {
            edit: editEntry.edit,
            range: editEntry.range,
          });
          return;
        }
        case 'insertTableCell': {
          AlineTransforms.rejectSuggestedInsertTableCell(editor, {
            editEntry,
          });
          return;
        }
        case 'removeTableCell': {
          AlineTransforms.rejectSuggestedRemoveTableCell(editor, {
            editEntry,
          });
          return;
        }
        case 'insertTableRow': {
          AlineTransforms.rejectSuggestedInsertTableRow(editor, {
            editEntry,
          });
          return;
        }
        case 'removeTableRow': {
          AlineTransforms.rejectSuggestedRemoveTableRow(editor, { editEntry });
          return;
        }
        default:
          assertNeverSafely(editEntry.edit);
      }
    });
  },

  /**
   * Rejects an array of suggested edits in a batch.
   */
  rejectSuggestedEdits: (
    editor: Editor,
    options: { authorId: UUID; editEntries: EditEntries; editingMode: EditingMode }
  ): SuggestionSource[] => {
    return AlineTransforms.withoutNormalizing<SuggestionSource[]>(editor, () => {
      const { authorId, editEntries, editingMode } = options;
      const rejectedSources: SuggestionSource[] = [];
      const orderedEntries = orderEntriesForRejection(editEntries);
      const editEntryRefs = orderedEntries.map(editEntry => ({
        ...editEntry,
        range: Editor.rangeRef(editor, editEntry.range),
      }));

      for (const editEntryRef of editEntryRefs) {
        const range = editEntryRef.range.unref();
        if (!range) continue;

        AlineTransforms.rejectSuggestedEdit(editor, {
          authorId,
          editEntry: { ...editEntryRef, range },
          editingMode,
        });
        if (isEditWithSource(editEntryRef.edit)) rejectedSources.push(editEntryRef.edit.source);
      }

      return rejectedSources;
    });
  },

  /**
   * Reject a suggested footers part edit.
   */
  rejectSuggestedFootersPartEdit: (
    editor: Editor,
    options: {
      editingMode: EditingMode;
      footersPartEditEntry: FootersPartEditEntry;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { footersPartEditEntry } = options;
      const { edit } = footersPartEditEntry;
      const footersPartEntry = Queries.getFootersElement(editor);
      if (!footersPartEntry) return;

      const [footersPart, footersPartPath] = footersPartEntry;
      if (!footersPart.suggestedEdits) return;
      const editIndex = footersPart.suggestedEdits.findIndex(suggestedEdit =>
        equals(suggestedEdit, edit)
      );
      if (editIndex === -1) return;

      const newSuggestedEdits = remove(editIndex, 1, footersPart.suggestedEdits);

      Transforms.setNodes(
        editor,
        { suggestedEdits: newSuggestedEdits },
        { at: footersPartPath, voids: true }
      );
    });
  },

  /**
   * Reject a suggested headers part edit.
   */
  rejectSuggestedHeadersPartEdit: (
    editor: Editor,
    options: {
      editingMode: EditingMode;
      headersPartEditEntry: HeadersPartEditEntry;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { headersPartEditEntry } = options;
      const { edit } = headersPartEditEntry;
      const headersPartEntry = Queries.getHeadersElement(editor);
      if (!headersPartEntry) return;

      const [headersPart, headersPartPath] = headersPartEntry;
      if (!headersPart.suggestedEdits) return;
      const editIndex = headersPart.suggestedEdits.findIndex(suggestedEdit =>
        equals(suggestedEdit, edit)
      );
      if (editIndex === -1) return;

      const newSuggestedEdits = remove(editIndex, 1, headersPart.suggestedEdits);

      Transforms.setNodes(
        editor,
        { suggestedEdits: newSuggestedEdits },
        { at: headersPartPath, voids: true }
      );
    });
  },

  /**
   * Rejects a suggested bookmark insertion by removing the bookmark markers.
   */
  rejectSuggestedInsertBookmark: (
    editor: Editor,
    options: { insertBookmarkEntry: InsertBookmarkEditEntry }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { insertBookmarkEntry } = options;
      const { range } = insertBookmarkEntry;
      const bookmark = Editor.above(editor, {
        at: Range.start(range),
        match: isBookmarkMarkerElement,
        voids: true,
      });

      Transforms.removeNodes(editor, {
        at: bookmark ? bookmark[1] : range,
        hanging: true,
        match: isBookmarkMarkerElement,
        voids: true,
      });
    });
  },

  /**
   * Rejects a suggested break (e.g., page break).
   */
  rejectSuggestedInsertBreak: (
    editor: Editor,
    options: { editingMode: EditingMode; insertBreakEntry: InsertBreakEditEntry }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { insertBreakEntry } = options;
      const { range } = insertBreakEntry;

      Transforms.removeNodes(editor, {
        at: range,
        hanging: true,
        match: isBreakElement,
        voids: true,
      });
    });
  },

  rejectSuggestedInsertCheckbox: (
    editor: Editor,
    options: { editingMode: EditingMode; insertCheckboxEntry: InsertCheckboxEditEntry }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { insertCheckboxEntry } = options;
      const { range } = insertCheckboxEntry;

      Transforms.removeNodes(editor, {
        at: range,
        hanging: true,
        match: isCheckboxElement,
        voids: true,
      });
    });
  },

  rejectSuggestedInsertDrawing: (
    editor: Editor,
    options: { editingMode: EditingMode; insertDrawingEntry: InsertDrawingEditEntry }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { insertDrawingEntry } = options;
      const { range } = insertDrawingEntry;

      Transforms.removeNodes(editor, {
        at: range,
        hanging: true,
        match: isDrawingElement,
        voids: true,
      });
    });
  },

  /**
   * Rejects a suggested instruction field insertion.
   */
  rejectSuggestedInsertInstructionField: (
    editor: Editor,
    options: {
      editingMode: EditingMode;
      insertInstructionFieldEntry: InsertInstructionFieldEditEntry;
    }
  ): void => {
    Editor.withoutNormalizing(editor, () => {
      const { insertInstructionFieldEntry } = options;
      const { range } = insertInstructionFieldEntry;

      Transforms.removeNodes(editor, {
        at: range,
        hanging: true,
        match: isInstructionFieldElement,
        voids: true,
      });
    });
  },

  /**
   * Rejects a suggested paragraph insertion (split). For every paragraph in
   * the entry's range carrying a same-author `insertParagraph` edit, strips
   * the edit and re-merges that paragraph with the one that follows it.
   */
  rejectSuggestedInsertParagraph: (
    editor: Editor,
    options: { insertParagraphEditEntry: InsertParagraphEditEntry }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { insertParagraphEditEntry } = options;
      const { edit, range } = insertParagraphEditEntry;
      const paragraphPaths = removeMatchingGlyphSuggestedEdits(editor, range, {
        authorId: edit.authorId,
        type: 'insertParagraph',
      });
      mergeEachWithNextParagraph(editor, paragraphPaths);
    });
  },

  /**
   * Rejects a suggested tab
   */
  rejectSuggestedInsertTab: (
    editor: Editor,
    options: { editingMode: EditingMode; insertTabEntry: InsertTabEditEntry }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { insertTabEntry } = options;
      const { range } = insertTabEntry;

      Transforms.removeNodes(editor, {
        at: range,
        hanging: true,
        match: isTabElement,
        voids: true,
      });
    });
  },

  /**
   * Rejects a suggested insertTableCell edit by removing the cell from the
   * document.
   */
  rejectSuggestedInsertTableCell: (editor: Editor, options: { editEntry: EditEntry }): void => {
    removeTableCellsInRange(editor, options.editEntry);
  },

  /**
   * Rejects a suggested table row insertion.
   * Deletes rows in the table within the suggested edit range.
   */
  rejectSuggestedInsertTableRow: (
    editor: Editor,
    options: {
      editEntry: EditEntry;
    }
  ) => {
    removeTableRowsInRange(editor, options.editEntry);
  },

  /**
   * Rejects a suggested insertion.
   */
  rejectSuggestedInsertion: (
    editor: Editor,
    options: {
      authorId: UUID;
      editingMode: EditingMode;
      insertEditEntry: InsertEditEntry;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { authorId, editingMode, insertEditEntry } = options;
      const { range } = insertEditEntry;
      const [start, end] = Range.edges(range);

      if (Path.equals(start.path, end.path)) {
        const textNode = Node.leaf(editor, start.path);
        const removedText = textNode.text.slice(start.offset, end.offset);

        // Using apply directly when we can maintains a better history stack (e.g., successive deletions using the cursor can be undone in one go).
        editor.apply({
          offset: start.offset,
          path: start.path,
          text: removedText,
          type: 'remove_text',
        });

        /**
         * We have an empty text node.  However, we don't remove it just yet as other functions that call this function use rangeRefs and
         * removing can cause the rangeRef to become null.  Instead, we leave it empty and remove all suggested edits and let the normalization
         * remove the empty text node after.
         */
        if (removedText === textNode.text)
          Transforms.setNodes(
            editor,
            { suggestedEdits: [] },
            {
              at: start.path,
              hanging: true,
              match: node =>
                Text.isText(node) &&
                Array.isArray(node.suggestedEdits) &&
                !isEmpty(node.suggestedEdits),
              voids: true,
            }
          );

        return;
      }

      const rangeAfterSplit = AlineTransforms.splitTextAtEdges(editor, {
        at: range,
        editingMode,
        hanging: true,
        voids: true,
      });
      if (!rangeAfterSplit) return;

      /**
       * Clear suggested edits from the nodes in the range first so, after the delete, we don't have a node left
       * with a suggested insertion.
       */
      Transforms.setNodes(
        editor,
        { suggestedEdits: [] },
        {
          at: rangeAfterSplit,
          hanging: true,
          match: node =>
            Text.isText(node) &&
            Array.isArray(node.suggestedEdits) &&
            !isEmpty(node.suggestedEdits),
          voids: true,
        }
      );

      AlineTransforms.deleteContent(editor, {
        at: rangeAfterSplit,
        authorId,
        editingMode: EditingMode.EDITING,
        hanging: true,
        voids: true,
      });
    });
  },

  /**
   * Rejects a suggested link edit.
   */
  rejectSuggestedLinkEdit: (
    editor: Editor,
    options: { editingMode: EditingMode; linkEditEntry: LinkEditEntry }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { editingMode, linkEditEntry } = options;
      const { edit, range } = linkEditEntry;
      const linkEntries = [
        ...Editor.nodes<LinkElement>(editor, {
          at: range,
          match: node =>
            Element.isElement(node) &&
            node.type === linkType &&
            Array.isArray(node.suggestedEdits) &&
            !isEmpty(node.suggestedEdits),
          voids: true,
        }),
      ];

      switch (edit.type) {
        case 'removeLink': {
          const linkFormats: Partial<AlineTextFormats> = {
            fontColor: {
              color: linkColor,
              origin: 'USER_APPLIED',
            },
            underline: {
              active: true,
              origin: 'USER_APPLIED',
            },
          };

          for (const [link, linkPath] of linkEntries) {
            if (!link.suggestedEdits) continue;
            const editIndex = link.suggestedEdits.findIndex(suggestedEdit =>
              equals(suggestedEdit, edit)
            );
            if (editIndex === -1) continue;

            const newSuggestedEdits = remove(editIndex, 1, link.suggestedEdits);

            Transforms.setNodes(
              editor,
              { suggestedEdits: newSuggestedEdits },
              { at: linkPath, voids: true }
            );

            // Reset the formats on the text inside the link to be link formats.
            Transforms.setNodes(editor, linkFormats, {
              at: linkPath,
              match: Text.isText,
              split: true,
              voids: true,
            });
          }

          break;
        }
        case 'linkEdit': {
          for (const [link, linkPath] of linkEntries) {
            if (!link.suggestedEdits) continue;
            const editIndex = link.suggestedEdits.findIndex(suggestedEdit =>
              equals(suggestedEdit, edit)
            );
            if (editIndex === -1) continue;

            const newSuggestedEdits = remove(editIndex, 1, link.suggestedEdits);

            Transforms.setNodes(
              editor,
              { suggestedEdits: newSuggestedEdits },
              { at: linkPath, voids: true }
            );
          }

          break;
        }
        case 'insertLink': {
          const pathRefs = linkEntries.map(([, linkPath]) => Editor.pathRef(editor, linkPath));

          for (const pathRef of pathRefs) {
            const linkPath = pathRef.unref();
            if (!linkPath) continue;

            /**
             * There is no need to update suggested edits.  We simply remove the link.
             */
            AlineTransforms.unwrapLink(editor, { editingMode, linkPath });
          }

          break;
        }
      }
    });
  },

  /**
   * Rejects a suggested paragraph edit.
   */
  rejectSuggestedParagraphFormatEdit: (
    editor: Editor,
    options: { editingMode: EditingMode; paragraphEditEntry: ParagraphFormatEditEntry }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { editingMode, paragraphEditEntry } = options;
      const { edit, range } = paragraphEditEntry;
      const paragraphEntries = Editor.nodes<ParagraphElement>(editor, {
        at: range,
        match: node =>
          Element.isElement(node) &&
          node.type === paragraphType &&
          Array.isArray(node.suggestedEdits) &&
          !isEmpty(node.suggestedEdits),
        voids: true,
      });
      const pathRefs = paragraphEntries.map(([, path]) => Editor.pathRef(editor, path)).toArray();

      for (const pathRef of pathRefs) {
        const paragraphPath = pathRef.unref();
        if (!paragraphPath) continue;

        const paragraph = Node.get(editor, paragraphPath);
        if (!isParagraphElement(paragraph) || !paragraph.suggestedEdits) continue;

        const editIndex = paragraph.suggestedEdits.findIndex(suggestedEdit =>
          equals(suggestedEdit, edit)
        );
        if (editIndex === -1) continue;

        const newSuggestedEdits = remove(editIndex, 1, paragraph.suggestedEdits);

        Transforms.setNodes(
          editor,
          { suggestedEdits: newSuggestedEdits },
          { at: paragraphPath, voids: true }
        );

        if (edit.type === 'variant') {
          AlineTransforms.applyStyleVariantToText(editor, {
            editingMode,
            paragraphEntry: [paragraph, paragraphPath],
            styleVariant: paragraph.variant,
          });
        }
      }
    });
  },

  /**
   * Rejects a remove break suggestion (e.g., page break).
   */
  rejectSuggestedRemoveBreak: (
    editor: Editor,
    options: { editingMode: EditingMode; removeBreakEntry: RemoveBreakEditEntry }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { removeBreakEntry } = options;
      const { edit, range } = removeBreakEntry;
      const breakEntries = [
        ...Editor.nodes<BreakElement>(editor, {
          at: range,
          match: node =>
            isBreakElement(node) &&
            Array.isArray(node.suggestedEdits) &&
            !isEmpty(node.suggestedEdits),
          voids: true,
        }),
      ];

      for (const [node, path] of breakEntries) {
        if (!node.suggestedEdits) continue;
        const breakEditIndex = node.suggestedEdits.findIndex(suggestedEdit =>
          equals(suggestedEdit, edit)
        );
        if (breakEditIndex === -1) continue;

        const newSuggestedEdits = remove(breakEditIndex, 1, node.suggestedEdits);

        Transforms.setNodes(
          editor,
          { suggestedEdits: newSuggestedEdits },
          { at: path, voids: true }
        );
      }
    });
  },

  /**
   * Rejects a suggested image removal.
   */
  rejectSuggestedRemoveDrawing: (
    editor: Editor,
    options: { editingMode: EditingMode; removeDrawingEntry: RemoveDrawingEditEntry }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { removeDrawingEntry } = options;
      const { edit, range } = removeDrawingEntry;
      const drawingEntries = [
        ...Editor.nodes<DrawingElement>(editor, {
          at: range,
          match: node =>
            isDrawingElement(node) &&
            Array.isArray(node.suggestedEdits) &&
            !isEmpty(node.suggestedEdits),
          voids: true,
        }),
      ];

      for (const [node, path] of drawingEntries) {
        if (!node.suggestedEdits) continue;
        const drawingEditIndex = node.suggestedEdits.findIndex(suggestedEdit =>
          equals(suggestedEdit, edit)
        );
        if (drawingEditIndex === -1) continue;

        const newSuggestedEdits = remove(drawingEditIndex, 1, node.suggestedEdits);

        Transforms.setNodes(
          editor,
          { suggestedEdits: newSuggestedEdits },
          { at: path, voids: true }
        );
      }
    });
  },

  /**
   * Rejects a suggested instruction field removal.
   */
  rejectSuggestedRemoveInstructionField: (
    editor: Editor,
    options: {
      editingMode: EditingMode;
      removeInstructionFieldEntry: RemoveInstructionFieldEditEntry;
    }
  ) => {
    const { removeInstructionFieldEntry } = options;
    const { edit, range } = removeInstructionFieldEntry;

    const instructionFieldEntries = [
      ...Editor.nodes<InstructionFieldElement>(editor, {
        at: range,
        match: node =>
          isInstructionFieldElement(node) &&
          node.instruction === edit.instruction &&
          Array.isArray(node.suggestedEdits) &&
          !isEmpty(node.suggestedEdits),
      }),
    ];

    for (const [node, path] of instructionFieldEntries) {
      if (!node.suggestedEdits) continue;
      const instructionFieldEditIndex = node.suggestedEdits.findIndex(suggestedEdit =>
        equals(suggestedEdit, edit)
      );
      if (instructionFieldEditIndex === -1) continue;

      const newSuggestedEdits = remove(instructionFieldEditIndex, 1, node.suggestedEdits);

      Transforms.setNodes(editor, { suggestedEdits: newSuggestedEdits }, { at: path, voids: true });
    }
  },

  /**
   * Rejects a remove break suggestion (e.g., page break).
   */
  rejectSuggestedRemoveTab: (
    editor: Editor,
    options: { editingMode: EditingMode; removeTabEntry: RemoveTabEditEntry }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { removeTabEntry } = options;
      const { edit, range } = removeTabEntry;
      const tabEntries = [
        ...Editor.nodes<TabElement>(editor, {
          at: range,
          match: node =>
            isTabElement(node) &&
            Array.isArray(node.suggestedEdits) &&
            !isEmpty(node.suggestedEdits),
          voids: true,
        }),
      ];

      for (const [node, path] of tabEntries) {
        if (!node.suggestedEdits) continue;
        const tabEditIndex = node.suggestedEdits.findIndex(suggestedEdit =>
          equals(suggestedEdit, edit)
        );
        if (tabEditIndex === -1) continue;

        const newSuggestedEdits = remove(tabEditIndex, 1, node.suggestedEdits);

        Transforms.setNodes(
          editor,
          { suggestedEdits: newSuggestedEdits },
          { at: path, voids: true }
        );
      }
    });
  },

  /**
   * Rejects a suggested removeTableCell edit by keeping the cell and removing
   * the edit marker from suggestedEdits.
   */
  rejectSuggestedRemoveTableCell: (editor: Editor, options: { editEntry: EditEntry }): void => {
    removeEditFromTableCells(editor, options.editEntry);
  },

  rejectSuggestedRemoveTableRow: (editor: Editor, options: { editEntry: EditEntry }) => {
    removeEditsFromTableRows(editor, options.editEntry);
  },

  /**
   * Rejects text format edits as specified in a text format edit entry.
   */
  rejectSuggestedTextFormatEdit: (
    editor: Editor,
    options: { editingMode: EditingMode; textFormatEditEntry: TextFormatEditEntry }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { textFormatEditEntry } = options;
      const { edit, range } = textFormatEditEntry;
      const textEntries = Editor.nodes<AlineText>(editor, {
        at: range,
        match: node =>
          Text.isText(node) && Array.isArray(node.suggestedEdits) && !isEmpty(node.suggestedEdits),
        voids: true,
      });

      for (const [textNode, textPath] of textEntries) {
        if (!textNode.suggestedEdits) continue;
        const editIndex = textNode.suggestedEdits.findIndex(suggestedEdit =>
          equals(suggestedEdit, edit)
        );
        if (editIndex === -1) continue;

        const newSuggestedEdits = remove(editIndex, 1, textNode.suggestedEdits);

        Transforms.setNodes(
          editor,
          { suggestedEdits: newSuggestedEdits },
          { at: textPath, voids: true }
        );
      }
    });
  },

  /**
   * Utility function to remove suggested edits that conflict with a prop(s) that is going to be applied to a node.
   * We need to do this, for example, when a user is in EditingMode.Editing mode and makes a change to a node prop that conflicts
   * with an already-existing suggested edit on that same node.
   */
  removeConflictingSuggestions: (
    editor: Editor,
    options: {
      editingMode: EditingMode;
      entry: NodeEntry<AlineDescendant>;
      props: DeepPartial<AlineElement> | Partial<AlineTextFormats>;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { editingMode, entry, props } = options;
      const [descendant, descendantPath] = entry;

      // The Array.isArray check is solely for our Hyperscript tests (some nodes in the tests don't have a suggestedEdits array).
      if (
        editingMode !== EditingMode.EDITING ||
        !Array.isArray(descendant.suggestedEdits) ||
        isEmpty(descendant.suggestedEdits)
      )
        return;

      type DeepPartialDescendant =
        | DeepPartial<Omit<AlineElement, 'children'>>
        | DeepPartial<Omit<AlineText, 'text'>>;
      type DeepPartialDescendantOrDescendantValue =
        | DeepPartialDescendant
        | ValueOf<DeepPartialDescendant>;
      const deeplyOmitConflictingProperties = (
        obj1: SuggestedEditValue,
        obj2: SuggestedEditValue
      ): SuggestedEditValue => {
        /**
         * Removes conflicts between object o1, o2 and returns a subset of o2 that does not have conflicting keys
         * with o1.
         */
        const omitConflicts = (
          o1: DeepPartialDescendantOrDescendantValue,
          o2: DeepPartialDescendantOrDescendantValue
        ): DeepPartialDescendantOrDescendantValue => {
          const getIsDeepPartialDescendant = (
            val: DeepPartialDescendantOrDescendantValue
          ): val is DeepPartialDescendant => isPlainObject(val) && val !== null;
          const getIsDescendantKey = (
            key: string,
            obj: DeepPartialDescendant
          ): key is keyof DeepPartialDescendant => {
            return key in obj;
          };

          if (Array.isArray(o1) && Array.isArray(o2)) return o2;
          else if (getIsDeepPartialDescendant(o1) && getIsDeepPartialDescendant(o2)) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const result: { [key: string]: any } = {};

            for (const key in o2) {
              if (getIsDescendantKey(key, o1)) {
                if (getIsDeepPartialDescendant(o1[key]) && getIsDeepPartialDescendant(o2[key])) {
                  const omitResult = omitConflicts(o1[key], o2[key]);
                  if (!isEmpty(omitResult)) result[key] = omitResult;
                }
              } else if (getIsDescendantKey(key, o2)) {
                result[key] = o2[key];
              }
            }

            return result;
          }

          return o2;
        };

        return omitConflicts(obj1, obj2) ?? {};
      };
      const newEdits = descendant.suggestedEdits.reduce<SuggestedEdit[]>(
        (newSuggestedEdits, existingEdit) => {
          const newValue = deeplyOmitConflictingProperties(props, existingEdit.value);
          const isEditValueUnchanged = equals(existingEdit.value, newValue);
          // No change to existingEdit.value.  Keep the edit.
          if (isEditValueUnchanged) {
            newSuggestedEdits.push(existingEdit);
            return newSuggestedEdits;
          }
          // newValue is changed, but all properties conflict with props so newValue === {}.  Remove edit.
          if (isEmpty(newValue)) return newSuggestedEdits;

          const newEdit = { ...existingEdit, value: newValue } as SuggestedEdit;
          newSuggestedEdits.push(newEdit);
          return newSuggestedEdits;
        },
        []
      );

      Transforms.setNodes(
        editor,
        { suggestedEdits: newEdits },
        { at: descendantPath, voids: true }
      );
    });
  },

  suggestCheckboxStateChange: (
    editor: Editor,
    options: { authorId: string; checkboxPath: Path; textToInsert: CheckboxStateText }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { authorId, checkboxPath, textToInsert } = options;
      const checkbox = Node.get(editor, checkboxPath);
      if (!isCheckboxElement(checkbox)) return;

      const stateText = Node.getIf(editor, checkboxPath.concat(0));
      if (!Text.isText(stateText)) return;

      const withoutToggle = checkbox.suggestedEdits.filter(
        edit => !isToggleCheckboxEdit(edit) && !isRemoveCheckboxEdit(edit)
      );
      if (textToInsert === stateText.text) {
        if (withoutToggle.length !== checkbox.suggestedEdits.length) {
          Transforms.setNodes(
            editor,
            { suggestedEdits: withoutToggle },
            { at: checkboxPath, voids: true }
          );
        }
        return;
      }

      const now = new Date().toISOString();
      Transforms.setNodes(
        editor,
        {
          suggestedEdits: [
            ...withoutToggle,
            {
              authorId,
              createdAt: now,
              type: 'toggleCheckbox',
              updatedAt: now,
              value: { to: textToInsert },
            } satisfies ToggleCheckboxEdit,
          ],
        },
        { at: checkboxPath, voids: true }
      );
    });
  },

  /**
   * Suggest deletion of content at a location.
   */
  suggestDeletion: (
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      at?: Location;
      distance?: number;
      reverse?: boolean;
      select?: boolean;
      source?: SuggestionSource;
      // We intentionally do not expose the 'line' unit as it doesn't work correctly in Slate.
      unit?: 'character' | 'word' | 'block';
      voids?: boolean;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const {
        authorId,
        editingMode,
        reverse = false,
        select = true,
        source,
        voids = true,
      } = options;
      const at = Queries.locationToDelete(editor, options);
      if (!at) return;

      const rangeAfterSplit = AlineTransforms.splitTextAtEdges(editor, {
        at,
        editingMode,
        hanging: true,
        voids,
      });
      if (!rangeAfterSplit) return;

      const rangeRef = Editor.rangeRef(editor, rangeAfterSplit, { affinity: 'inward' });
      const editEntries = Queries.sortedEditRanges(editor, { at: rangeAfterSplit, voids });
      const insertEntriesByUser = editEntries.filter(
        (
          editEntry
        ): editEntry is
          | InsertEditEntry
          | InsertBreakEditEntry
          | InsertCheckboxEditEntry
          | InsertDrawingEditEntry
          | InsertTabEditEntry
          | InsertBookmarkEditEntry
          | InsertInstructionFieldEditEntry => {
          const isInsertEntry =
            isInsertEditEntry(editEntry) ||
            isInsertTabEditEntry(editEntry) ||
            isInsertBookmarkEditEntry(editEntry) ||
            isInsertBreakEditEntry(editEntry) ||
            isInsertCheckboxEditEntry(editEntry) ||
            isInsertDrawingEditEntry(editEntry) ||
            isInsertInstructionFieldEditEntry(editEntry);

          if (!isInsertEntry || editEntry.edit.authorId !== authorId) return false;

          // For text insertions, check source compatibility to determine if we
          // should cancel (reject) or layer a delete on top. This prevents AI
          // edits (which have a source) from rejecting user insertions (no source).
          if (isInsertEditEntry(editEntry)) {
            return getIsCancellableEdit(editEntry.edit.source, source);
          }

          return true;
        }
      );
      const insertEntryRefs = insertEntriesByUser.map(editEntry => ({
        editEntry,
        insertRangeRef: Editor.rangeRef(editor, editEntry.range),
      }));

      for (const { editEntry, insertRangeRef } of insertEntryRefs) {
        const range = insertRangeRef.unref();
        if (!range) continue;

        switch (editEntry.edit.type) {
          case 'insertNode': {
            AlineTransforms.rejectSuggestedInsertion(editor, {
              authorId,
              editingMode,
              insertEditEntry: { edit: editEntry.edit, range },
            });
            break;
          }
          case 'insertBreak': {
            AlineTransforms.rejectSuggestedInsertBreak(editor, {
              editingMode,
              insertBreakEntry: { edit: editEntry.edit, range },
            });
            break;
          }
          case 'insertDrawing': {
            AlineTransforms.rejectSuggestedInsertDrawing(editor, {
              editingMode,
              insertDrawingEntry: { edit: editEntry.edit, range },
            });
            break;
          }
          case 'insertTab': {
            AlineTransforms.rejectSuggestedInsertTab(editor, {
              editingMode,
              insertTabEntry: { edit: editEntry.edit, range },
            });
            break;
          }
          case 'insertBookmark': {
            AlineTransforms.rejectSuggestedInsertBookmark(editor, {
              insertBookmarkEntry: { edit: editEntry.edit, range },
            });
            break;
          }
          case 'insertInstructionField': {
            AlineTransforms.rejectSuggestedInsertInstructionField(editor, {
              editingMode,
              insertInstructionFieldEntry: { edit: editEntry.edit, range },
            });
            break;
          }
          case 'insertCheckbox': {
            AlineTransforms.rejectSuggestedInsertCheckbox(editor, {
              editingMode,
              insertCheckboxEntry: { edit: editEntry.edit, range },
            });
            break;
          }
          default:
            assertNeverSafely(editEntry.edit);
        }
      }

      /**
       * Cancel same-author paragraph-mark insertions inside the range: deleting a
       * break you suggested removes it (merging paragraphs back), like the
       * rejections above undo your own insertions. Without this, paragraphs keep
       * `insertParagraph` marks while their text gets `deleteNode` edits — a
       * contradictory "inserted and deleted" state.
       *
       * One range-wide pass so `mergeEachWithNextParagraph` collapses the chain in
       * reverse document order; per-entry would shift each later entry's range as
       * earlier paragraphs merge away.
       */
      let didCancelInsertedParagraphs = false;
      if (rangeRef.current) {
        const cancelledParagraphPaths = removeMatchingGlyphSuggestedEdits(
          editor,
          rangeRef.current,
          {
            authorId,
            type: 'insertParagraph',
          }
        );
        if (!isEmpty(cancelledParagraphPaths)) {
          mergeEachWithNextParagraph(editor, cancelledParagraphPaths);
          didCancelInsertedParagraphs = true;
        }
      }

      /**
       * This is tricky, but if insertRangeRefs is non-empty and rangeRef.current is collapsed or abutting, it means we've
       * deleted all relevant content when processing insertRangeRefs.  So, we return early so that we don't create unnecessary
       * empty nodes with delete suggestions.
       */
      if (
        !rangeRef.current ||
        ((!isEmpty(insertEntryRefs) || didCancelInsertedParagraphs) &&
          (Range.isCollapsed(rangeRef.current) ||
            Queries.rangePointsAbutting(editor, rangeRef.current)))
      ) {
        const range = rangeRef.unref();
        if (range) {
          const [start, end] = Range.edges(range);
          const selectablePoint = Queries.getSelectablePoint(editor, {
            isDeletion: true,
            point: reverse ? start : end,
            reverse,
          });
          if (select) AlineTransforms.select(editor, selectablePoint);
        }

        return;
      }

      const textEntries = [
        ...Editor.nodes<AlineText>(editor, {
          at: rangeRef.current,
          match: Text.isText,
          voids,
        }),
      ];

      const rowPathToGroupId = new Map<string, string>();
      if (rangeRef.current) {
        const tableEntries = [
          ...Editor.nodes<TableElement>(editor, {
            at: rangeRef.current,
            match: isTableElement,
          }),
        ];

        for (const [table, tablePath] of tableEntries) {
          const rowEntries = arrayEntries(table.children);
          const allRowsInRange = rowEntries.every(([rowIndex]) => {
            if (!rangeRef.current) return false;
            const rowRange = Editor.range(editor, [...tablePath, rowIndex]);
            return Range.surrounds(rangeRef.current, rowRange);
          });
          if (!allRowsInRange) continue;

          const groupId = nanoid();
          for (const [rowIndex, row] of rowEntries) {
            const rowPath = [...tablePath, rowIndex];
            const removeRowEdit: RemoveTableRowEdit = {
              authorId,
              groupId,
              source,
              type: 'removeTableRow',
              value: {},
            };
            const newSuggestedEdits = Queries.newEdits(row, removeRowEdit);
            if (newSuggestedEdits) {
              Transforms.setNodes(editor, { suggestedEdits: newSuggestedEdits }, { at: rowPath });
            }
            rowPathToGroupId.set(rowPath.join(','), groupId);
          }
        }
      }

      for (const [textNode, textPath] of textEntries) {
        const tableRowAbove = Editor.above(editor, {
          at: textPath,
          match: isTableRowElement,
        });
        const rowGroupId = tableRowAbove
          ? rowPathToGroupId.get(tableRowAbove[1].join(','))
          : undefined;

        addDeletionSuggestionToTextNode(editor, {
          authorId,
          groupId: rowGroupId,
          source,
          textNode,
          textPath,
        });
      }

      /**
       * Mark the trailing pilcrow of every paragraph the deletion range
       * extends past, so the pending paragraph merge is recorded on the
       * paragraph mark itself (Word's model, and the shape the DOCX importer
       * produces) instead of being inferred at accept time from text-mark
       * contiguity. Without this, an exported DOCX shows the text struck but
       * the paragraph marks intact, and accepting it in Word leaves empty
       * paragraphs behind.
       */
      if (rangeRef.current) {
        const rangeEnd = Range.end(rangeRef.current);
        const paragraphEntries = [
          ...Editor.nodes<ParagraphElement>(editor, {
            at: rangeRef.current,
            match: isParagraphElement,
          }),
        ];
        for (const [, paragraphPath] of paragraphEntries) {
          // Only pilcrows the range extends past are being deleted.
          if (!Point.isAfter(rangeEnd, Editor.end(editor, paragraphPath))) continue;
          /**
           * Accepting a `deleteParagraph` merges the paragraph into its next
           * sibling, so only stamp when that sibling is a paragraph. This
           * also keeps a cell's last paragraph from merging into the next
           * cell and a paragraph from merging into a following table.
           */
          const nextPath = Path.next(paragraphPath);
          if (!Node.has(editor, nextPath) || !isParagraphElement(Node.get(editor, nextPath))) {
            continue;
          }
          /**
           * Rows marked for removal own their entire content; their internal
           * paragraph structure is removed with the row, not merged away.
           */
          const rowAbove = Editor.above(editor, { at: paragraphPath, match: isTableRowElement });
          if (rowAbove && rowPathToGroupId.has(rowAbove[1].join(','))) continue;
          markParagraphAsSuggestedDelete(editor, paragraphPath, { authorId, source });
        }
      }

      const range = rangeRef.unref();
      if (range) {
        const [start, end] = Range.edges(range);
        const selectablePoint = Queries.getSelectablePoint(editor, {
          isDeletion: true,
          point: reverse ? start : end,
          reverse,
        });
        if (select) AlineTransforms.select(editor, selectablePoint);
      }
    });
  },

  /**
   * Adds deletion suggestions (`deleteNode` and void removal edits) to all
   * descendant content within a path. Used by table row/column deletion to mark
   * cell content as deleted alongside the structural `removeTableRow` /
   * `removeTableCell` edits.
   */
  suggestDeletionOfDescendants: (
    editor: Editor,
    options: { at: Path; authorId: string; groupId?: string; source?: SuggestionSource }
  ): void => {
    const { at, authorId, groupId, source } = options;
    const textEntries = [
      ...Editor.nodes<AlineText>(editor, {
        at,
        match: Text.isText,
        voids: true,
      }),
    ];

    for (const [textNode, textPath] of textEntries) {
      addDeletionSuggestionToTextNode(editor, {
        authorId,
        groupId,
        source,
        textNode,
        textPath,
      });
    }
  },

  /**
   * Suggest an edit to a document property at a location.
   */
  suggestDocumentEdit: (
    editor: Editor,
    options: { documentEdit: SuggestedDocEdit; editingMode: EditingMode }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { documentEdit } = options;
      const [document, documentPath] = Queries.getDocument(editor);
      const newSuggestedEdits = Queries.newEdits(document, documentEdit);
      if (!newSuggestedEdits) return;

      Transforms.setNodes(editor, { suggestedEdits: newSuggestedEdits }, { at: documentPath });
    });
  },

  /**
   * Suggest a footers element edit.
   */
  suggestFootersEdit: (
    editor: Editor,
    options: { editingMode: EditingMode; footersEdit: SuggestedFootersEdit }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { footersEdit } = options;
      const footersEntry = Queries.getFootersElement(editor);
      if (!footersEntry) return;

      const [footers, footersPath] = footersEntry;
      const newSuggestedEdits = Queries.newEdits(footers, footersEdit);
      if (!newSuggestedEdits) return;

      Transforms.setNodes(editor, { suggestedEdits: newSuggestedEdits }, { at: footersPath });
    });
  },

  /**
   * Suggest a headers element edit.
   */
  suggestHeadersEdit: (
    editor: Editor,
    options: { editingMode: EditingMode; headersEdit: SuggestedHeadersEdit }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { headersEdit } = options;
      const headersEntry = Queries.getHeadersElement(editor);
      if (!headersEntry) return;

      const [headers, headersPath] = headersEntry;
      const newSuggestedEdits = Queries.newEdits(headers, headersEdit);
      if (!newSuggestedEdits) return;

      Transforms.setNodes(editor, { suggestedEdits: newSuggestedEdits }, { at: headersPath });
    });
  },

  /**
   * Suggest an edit to a paragraph property at a location.
   */
  suggestParagraphEdit: (
    editor: Editor,
    options: { editingMode: EditingMode; paragraphEdit: SuggestedParagraphEdit; at?: Location }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { at = editor.selection, editingMode, paragraphEdit } = options;
      if (!at) return;

      const paragraphEntries = Queries.paragraphsAtLocation(editor, { at });

      for (const [paragraph, paragraphPath] of paragraphEntries) {
        const newSuggestedEdits = Queries.newEdits(paragraph, paragraphEdit);

        if (!newSuggestedEdits) continue;

        Transforms.setNodes(editor, { suggestedEdits: newSuggestedEdits }, { at: paragraphPath });

        if (paragraphEdit.type === 'variant') {
          AlineTransforms.applyStyleVariantToText(editor, {
            editingMode,
            paragraphEntry: [paragraph, paragraphPath],
            styleVariant: paragraphEdit.value.variant,
          });
        }
      }
    });
  },

  /**
   * Suggest an edit to text formatting at a location.
   */
  suggestTextFormatEdit: (
    editor: Editor,
    options: {
      editingMode: EditingMode;
      textFormatEdit: TextFormatEdit;
      at?: Location;
      voids?: boolean;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { at = editor.selection, editingMode, textFormatEdit, voids = true } = options;
      if (!at) return;

      const editKeys = keys(textFormatEdit.value);
      const shouldAddMark =
        at === editor.selection &&
        Range.isCollapsed(at) &&
        !Editor.above(editor, {
          at,
          match: n => Element.isElement(n) && editor.isVoid(n),
          voids: true,
        });

      /**
       * If the Location is the selection and it's collapsed, we simply add the suggested edit to editor.marks/the cursor as a normal edit (i.e., on the text node and not on the suggestedEdits key).
       * This is because, when a user is suggesting an edit at the cursor, we want the new text node after the user types to register only as 'insertNode'
       * and not contain other suggested edits.
       */
      if (shouldAddMark) {
        for (const editKey of editKeys) {
          Editor.addMark(editor, editKey, textFormatEdit.value[editKey]);
        }

        return;
      }

      // We need to split first so we apply properties only to the text nodes at the Location.
      const rangeAfterSplit = AlineTransforms.splitTextAtEdges(editor, {
        at,
        editingMode,
      });

      if (!rangeAfterSplit) return;

      const textEntries = [
        ...Editor.nodes<AlineText>(editor, {
          at: rangeAfterSplit,
          match: Text.isText,
          voids,
        }),
      ];

      const allTextInsertedByUser = textEntries.every(
        ([textNode]) =>
          textNode.suggestedEdits?.some(
            edit => edit.type === 'insertNode' && edit.authorId === textFormatEdit.authorId
          ) ?? false
      );

      for (const [textNode, textPath] of textEntries) {
        /**
         * If all text in the range has already been inserted by the user, we simply add the formatting to the text
         * without appending the edit to suggestedEdits.  This is because suggested insertions are treated as a single edit.
         */
        if (allTextInsertedByUser) {
          Transforms.setNodes(editor, textFormatEdit.value, { at: textPath, split: true, voids });

          continue;
        }

        /**
         * An empty table cell's lone text cannot carry a format suggestion — a zero-width suggestion
         * is unresolvable, so normalizeText strips it. Apply the format directly instead so
         * Queries.isTextFormatActive still reads the cell as formatted. An empty paragraph outside a
         * table is left to the suggestion path, where the stripping is the right outcome: formatting
         * it directly would write a change that never reaches the review panel and cannot be
         * rejected, surfacing only when someone later types on the line.
         */
        const [parentNode] = Editor.parent(editor, textPath);
        const isLoneEmptyCellText =
          textNode.text === '' &&
          isParagraphElement(parentNode) &&
          parentNode.children.length === 1 &&
          Boolean(Editor.above(editor, { at: textPath, match: isTableCellElement }));
        if (isLoneEmptyCellText) {
          Transforms.setNodes(editor, textFormatEdit.value, { at: textPath, split: true, voids });

          continue;
        }

        const newSuggestedEdits = Queries.newEdits(textNode, textFormatEdit);
        if (!newSuggestedEdits) continue;

        Transforms.setNodes(
          editor,
          { suggestedEdits: newSuggestedEdits },
          { at: textPath, split: true, voids }
        );
      }
    });
  },
};

export default TrackedChangesTransforms;

/**
 * Walks every paragraph in `range` and strips any glyph-edit matching
 * `type` + `authorId`. Returns the affected paragraph paths in document
 * order so the caller can perform follow-up work (e.g. merging paragraphs).
 */
function removeMatchingGlyphSuggestedEdits(
  editor: Editor,
  range: Range,
  predicate: { authorId: string; type: 'insertParagraph' | 'deleteParagraph' }
): Path[] {
  const paragraphEntries = Editor.nodes<ParagraphElement>(editor, {
    at: range,
    match: node =>
      isParagraphElement(node) &&
      (node.glyphProps?.suggestedEdits ?? []).some(
        edit => edit.type === predicate.type && edit.authorId === predicate.authorId
      ),
  });

  const affectedPaths: Path[] = [];
  for (const [paragraph, paragraphPath] of paragraphEntries) {
    const glyphEdits = paragraph.glyphProps?.suggestedEdits ?? [];
    const editIndex = glyphEdits.findIndex(
      edit => edit.type === predicate.type && edit.authorId === predicate.authorId
    );
    if (editIndex === -1) continue;

    setGlyphSuggestedEdits(editor, paragraphPath, remove(editIndex, 1, glyphEdits));
    affectedPaths.push(paragraphPath);
  }

  return affectedPaths;
}

/**
 * Merges the paragraph at `paragraphPath` with the next one. The survivor keeps
 * the leading paragraph's node, but its pilcrow is now the next paragraph's, so
 * copy the next `glyphProps` onto it first (`transferGlyphPropsForParagraphMerge`).
 * Without this, a chain of `insertParagraph` edits loses one edit per merge.
 * Glyph edits still on the absorbed pilcrow are dropped — the boundary they
 * describe no longer exists once the merge lands.
 */
function mergeWithNextParagraph(editor: Editor, paragraphPath: Path): void {
  const nextPath = Path.next(paragraphPath);
  if (!Node.has(editor, nextPath) || !isParagraphElement(Node.get(editor, nextPath))) {
    /**
     * No following paragraph to merge into — this is the last block of an
     * insertion (e.g. an AI insert appended after the anchor). Once its
     * inserted text has been rejected the block is empty, so remove it instead
     * of leaving a dangling empty paragraph. Guard on having a previous sibling
     * so we never empty the container.
     */
    const node = Node.get(editor, paragraphPath);
    if (isParagraphElement(node) && Queries.nodeIsEmpty(node) && Path.hasPrevious(paragraphPath)) {
      Transforms.removeNodes(editor, { at: paragraphPath });
    }
    return;
  }

  transferGlyphPropsForParagraphMerge(editor, { lowerPath: nextPath, upperPath: paragraphPath });
  Transforms.mergeNodes(editor, { at: nextPath, match: isParagraphElement });
}

function orderEntriesForRejection(editEntries: EditEntries): EditEntries {
  return [
    ...editEntries.filter(entry => !isParagraphGlyphEdit(entry.edit)),
    ...editEntries.filter(entry => isParagraphGlyphEdit(entry.edit)),
  ];
}

/**
 * Merges every paragraph at the given paths with the paragraph immediately
 * after it. Processes paths in reverse document order via `pathRef`s so
 * earlier paths stay valid while later paragraphs collapse.
 */
function mergeEachWithNextParagraph(editor: Editor, paragraphPaths: Path[]): void {
  const pathRefs = paragraphPaths.map(path => Editor.pathRef(editor, path));
  for (let i = pathRefs.length - 1; i >= 0; i--) {
    const path = pathRefs[i].unref();
    if (!path) continue;
    mergeWithNextParagraph(editor, path);
  }
}

function forMatchingCheckboxEdit(
  editor: Editor,
  options: { edit: SuggestedCheckboxEdit; range: Range },
  fn: (path: Path, editIndex: number, checkbox: CheckboxElement) => void
): void {
  const { edit, range } = options;
  for (const [node, path] of Editor.nodes<CheckboxElement>(editor, {
    at: range,
    match: isCheckboxElement,
    voids: true,
  })) {
    const editIndex = node.suggestedEdits?.findIndex(e => equals(e, edit)) ?? -1;
    if (editIndex === -1) continue;
    fn(path, editIndex, node);
  }
}

function removeCheckboxEditAt(
  editor: Editor,
  path: Path,
  editIndex: number,
  checkbox: CheckboxElement
): void {
  Transforms.setNodes(
    editor,
    { suggestedEdits: remove(editIndex, 1, checkbox.suggestedEdits) },
    { at: path, voids: true }
  );
}

function rejectMatchingCheckboxEdit(
  editor: Editor,
  options: { edit: SuggestedCheckboxEdit; range: Range }
): void {
  AlineTransforms.withoutNormalizing(editor, () => {
    forMatchingCheckboxEdit(editor, options, (path, editIndex, checkbox) =>
      removeCheckboxEditAt(editor, path, editIndex, checkbox)
    );
  });
}

function addDeletionSuggestionToTextNode(
  editor: Editor,
  options: {
    authorId: string;
    textNode: AlineText;
    textPath: Path;
    groupId?: string;
    source?: SuggestionSource;
  }
): void {
  const { authorId, groupId, source, textNode, textPath } = options;
  const groupIdSpread = groupId ? { groupId } : {};

  const voidAbove = Editor.above(editor, {
    at: textPath,
    match: n =>
      isPageBreakElement(n) ||
      isTabElement(n) ||
      isCheckboxElement(n) ||
      isDrawingElement(n) ||
      isInlineInstructionFieldElement(n),
    voids: true,
  });

  if (voidAbove) {
    const [element, elementPath] = voidAbove;
    applyVoidRemovalEdit(editor, element, elementPath, { authorId, groupIdSpread });
    return;
  }

  /**
   * Never tag an empty text node for deletion: there are no characters to
   * delete, so the mark carries no meaning — and because accept/reject
   * process edits by text range, a zero-width mark can never be resolved,
   * leaving an invisible pending suggestion that keeps the toolbar alive.
   * (The void check above must run first: void filler texts are empty by
   * design and their removal is recorded on the void element.)
   */
  if (textNode.text === '') return;

  const deleteEdit: DeleteEdit = {
    authorId,
    createdAt: new Date().toISOString(),
    ...groupIdSpread,
    source,
    type: 'deleteNode',
    updatedAt: new Date().toISOString(),
    value: {},
  };
  applySuggestedEdit(editor, textNode, textPath, deleteEdit);
}

function applyVoidRemovalEdit(
  editor: Editor,
  element: Node,
  elementPath: Path,
  options: { authorId: string; groupIdSpread: { groupId?: string } }
): void {
  const { authorId, groupIdSpread } = options;

  if (isPageBreakElement(element)) {
    const edit: RemoveBreakEdit = {
      authorId,
      createdAt: new Date().toISOString(),
      ...groupIdSpread,
      type: 'removeBreak',
      updatedAt: new Date().toISOString(),
      value: {},
      variant: BreakVariant.Page,
    };
    applySuggestedEdit(editor, element, elementPath, edit);
    return;
  }

  if (isTabElement(element)) {
    const edit: RemoveTabEdit = {
      authorId,
      ...groupIdSpread,
      type: 'removeTab',
      value: {},
    };
    applySuggestedEdit(editor, element, elementPath, edit);
    return;
  }

  if (isDrawingElement(element)) {
    const edit: RemoveDrawingEdit = {
      authorId,
      ...groupIdSpread,
      type: 'removeDrawing',
      value: {},
    };
    applySuggestedEdit(editor, element, elementPath, edit);
    return;
  }

  if (isCheckboxElement(element)) {
    const edit: RemoveCheckboxEdit = {
      authorId,
      createdAt: new Date().toISOString(),
      ...groupIdSpread,
      type: 'removeCheckbox',
      updatedAt: new Date().toISOString(),
      value: {},
    };
    applySuggestedEdit(editor, element, elementPath, edit);
    return;
  }

  if (isInlineInstructionFieldElement(element)) {
    const edit: RemoveInstructionFieldEdit = {
      authorId,
      ...groupIdSpread,
      instruction: element.instruction,
      type: 'removeInstructionField',
      value: {},
    };
    applySuggestedEdit(editor, element, elementPath, edit);
    return;
  }
}

function applySuggestedEdit<T extends AlineDescendant>(
  editor: Editor,
  node: T,
  path: Path,
  edit: NonNullable<T['suggestedEdits']>[number]
): void {
  const newSuggestedEdits = Queries.newEdits(node, edit);
  if (!newSuggestedEdits) return;

  Transforms.setNodes(editor, { suggestedEdits: newSuggestedEdits }, { at: path, voids: true });
}

/**
 * Removes table rows within a suggested edit range that have the matching edit.
 */
function removeTableRowsInRange(editor: Editor, editEntry: EditEntry): void {
  AlineTransforms.withoutNormalizing(editor, () => {
    const { edit, range } = editEntry;

    const rowEntries = [
      ...Editor.nodes(editor, {
        at: range,
        match: node =>
          !!(isTableRowElement(node) && node.suggestedEdits?.some(e => equals(e, edit))),
        mode: 'all',
      }),
    ];

    // Collect unique parent-table path refs before rows are removed.
    const tablePathRefMap = new Map<string, PathRef>();
    for (const [, rowPath] of rowEntries) {
      const tableEntry = Editor.above(editor, { at: rowPath, match: isTableElement });
      if (!tableEntry) continue;
      const key = tableEntry[1].join(',');
      if (!tablePathRefMap.has(key)) {
        tablePathRefMap.set(key, Editor.pathRef(editor, tableEntry[1]));
      }
    }

    for (const [, rowPath] of rowEntries.reverse()) {
      Transforms.removeNodes(editor, { at: rowPath });
    }

    // Remove any table now left with no rows.
    for (const tablePathRef of tablePathRefMap.values()) {
      const tablePath = tablePathRef.unref();
      if (!tablePath) continue;

      const tableNode = Node.getIf(editor, tablePath);
      if (!tableNode) continue;

      if (isTableElement(tableNode) && !tableNode.children.some(isTableRowElement)) {
        Transforms.removeNodes(editor, { at: tablePath });
      }
    }
  });
}

/**
 * Removes table cells within a suggested edit range
 */
function removeTableCellsInRange(editor: Editor, editEntry: EditEntry) {
  AlineTransforms.withoutNormalizing(editor, () => {
    const { edit, range } = editEntry;

    const cellEntries = [
      ...Editor.nodes(editor, {
        at: range,
        match: node =>
          !!(isTableCellElement(node) && node.suggestedEdits?.some(e => equals(e, edit))),
        mode: 'all',
      }),
    ];

    for (const [, cellPath] of cellEntries.reverse()) {
      Transforms.removeNodes(editor, { at: cellPath });
    }
  });
}

/**
 * Removes suggested edits for cells
 */
function removeEditFromTableCells(editor: Editor, editEntry: EditEntry) {
  AlineTransforms.withoutNormalizing(editor, () => {
    const { edit, range } = editEntry;

    const entries = Editor.nodes(editor, {
      at: range,
      match: node =>
        !!(isTableCellElement(node) && node.suggestedEdits?.some(e => equals(e, edit))),
      mode: 'all',
    });

    for (const [node, nodePath] of entries) {
      if (!isTableCellElement(node) || !node.suggestedEdits) continue;

      const editIndex = node.suggestedEdits.findIndex(e => equals(e, edit));
      if (editIndex === -1) continue;

      const nextSuggestedEdits = remove(editIndex, 1, node.suggestedEdits);
      Transforms.setNodes(editor, { suggestedEdits: nextSuggestedEdits }, { at: nodePath });
    }
  });
}

/**
 * Removes suggested edits for rows
 */
function removeEditsFromTableRows(editor: Editor, editEntry: EditEntry): void {
  AlineTransforms.withoutNormalizing(editor, () => {
    const { edit, range } = editEntry;
    const entries = [
      ...Editor.nodes<TableRowElement>(editor, {
        at: range,
        match: node =>
          isTableRowElement(node) &&
          Array.isArray(node.suggestedEdits) &&
          !isEmpty(node.suggestedEdits),
        voids: true,
      }),
    ];
    for (const [node, path] of entries) {
      if (!node.suggestedEdits) continue;
      const editIndex = node.suggestedEdits.findIndex(suggestedEdit => equals(suggestedEdit, edit));
      if (editIndex === -1) continue;

      const newSuggestedEdits = remove(editIndex, 1, node.suggestedEdits);

      Transforms.setNodes(editor, { suggestedEdits: newSuggestedEdits }, { at: path, voids: true });
    }
  });
}
